import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { NetEligibilityNotice } from './NetEligibilityNotice';
import { parseOptionalHandicap, hasHandicap, eventHandicap, handicapLabel, handicapBaseline } from '../utils/handicap';
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { setActiveRoundForYardage, clearActiveRound, updateActiveRoundHole } from "./RonRoHelpButton";
import { useFullScreen } from "./DarkAppShell";
import SharedHoleInfoCard from "./SharedHoleInfoCard";
import { 
  Flag,
  Trophy,
  Calendar as CalendarIcon,
  Trash2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Camera,
  X,
  Check,
  Users,
  Settings,
  ArrowLeft,
  Play,
  UserPlus,
  UserCircle,
  User,
  DollarSign,
  Sparkles,
  PartyPopper,
  MapPin,
  Edit,
  RefreshCw,
  ExternalLink,
  MoreVertical,
  Link2,
  Search,
  AlertTriangle,
  Wallet,
  Coins,
  CheckCircle,
  Target
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { format } from "date-fns";
import ScoreEntryForm from "./ScoreEntryForm";
import { AddPlayerModal } from "../App";
import { apiError } from '../utils/apiError';
import { SkinsCompletedResults } from './skins/SkinsCompletedResults';
import { CourseLookupModal, CourseParDisplay } from "./score-entry";
import UserBubble from "./UserBubble";
import { JoinTournamentButton, TournamentPrizeInfo } from "./JoinTournamentButton";
import SkinsSetupCarousel from "./SkinsSetupCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format currency/coins amount for display
const formatAmount = (amount, isCoins, showSign = false) => {
  if (isCoins) {
    const rounded = Math.round(amount);
    const sign = showSign && rounded > 0 ? '+' : '';
    return `${sign}${rounded.toLocaleString()} coins`;
  } else {
    const sign = showSign && amount > 0 ? '+' : '';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  }
};

// Hook to detect current theme
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    return !document.documentElement.classList.contains('theme-light');
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(!document.documentElement.classList.contains('theme-light'));
    };
    
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
};

// Helper function to calculate strokes received on each hole
const getStrokesOnHole = (playerHandicap, minHandicap, holeIdx, holeHandicaps) => {
  if (!hasHandicap(playerHandicap) || !hasHandicap(minHandicap)) return 0;
  if (!holeHandicaps || holeIdx >= holeHandicaps.length) {
    return 0;
  }
  
  // Treat null/undefined handicap as 0 (scratch golfer)
  const effectiveHandicap = playerHandicap;
  const effectiveMinHandicap = minHandicap;
  
  const strokesReceived = Math.floor(effectiveHandicap - effectiveMinHandicap);
  if (strokesReceived <= 0) return 0;
  
  // Hole handicap rating (1 = hardest, 18 = easiest)
  const holeHcpRating = holeHandicaps[holeIdx];
  
  // Player gets a stroke if hole_hcp_rating <= strokes_received
  if (holeHcpRating <= strokesReceived) {
    return 1;
  }
  // For >18 strokes, player gets 2 strokes on hardest holes
  if (strokesReceived > 18 && holeHcpRating <= (strokesReceived - 18)) {
    return 2;
  }
  return 0;
};

// Helper function to get score styling based on score vs par
// Returns { shape: 'circle' | 'square' | 'default', color: 'birdie' | 'par' | 'bogey' | 'double+' }
const getScoreStyle = (score, holePar) => {
  if (!score || !holePar) return { shape: 'default', scoreType: 'default' };
  
  const diff = score - holePar;
  
  if (diff <= -2) {
    // Eagle or better - highlighted circle
    return { shape: 'circle-double', scoreType: 'eagle' };
  } else if (diff === -1) {
    // Birdie - circle
    return { shape: 'circle', scoreType: 'birdie' };
  } else if (diff === 0) {
    // Par - square
    return { shape: 'square', scoreType: 'par' };
  } else if (diff === 1) {
    // Bogey
    return { shape: 'default', scoreType: 'bogey' };
  } else {
    // Double bogey or worse
    return { shape: 'default', scoreType: 'double' };
  }
};

const SkinsEventDetail = ({ eventId, onBack, user }) => {
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const fetchSequence = useRef(0);
  const mounted = useRef(true);
  const currentEventRef = useRef(null);
  const [activeTab, setActiveTab] = useState('players');
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  // Store scores per player { playerId: [scores] }
  const [playerScores, setPlayerScores] = useState({});
  const [draftState, setDraftState] = useState('loading');
  const [draftRetry, setDraftRetry] = useState(0);
  const [submittingPlayer, setSubmittingPlayer] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const calculationLock = useRef(false);
  // Winner celebration modal
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  
  // Theme detection
  const isDark = useTheme();
  
  // Full screen mode - hide navigation when in game
  const { setIsFullScreen } = useFullScreen();
  
  // Activate full screen mode when in setup or active status
  useEffect(() => {
    if (event?.status === 'setup' || event?.status === 'active') {
      setIsFullScreen(true);
    }
    // Cleanup: restore navigation when leaving
    return () => setIsFullScreen(false);
  }, [event?.status, setIsFullScreen]);
  
  // Expanded payment details per player
  const [expandedPayments, setExpandedPayments] = useState({});
  
  // Edit player handicap state
  const [editingHandicapPlayer, setEditingHandicapPlayer] = useState(null);
  const [newHandicapValue, setNewHandicapValue] = useState('');
  
  // Shared scan state - one scan for all players
  const [sharedScanResult, setSharedScanResult] = useState(null);
  const [sharedScanPreview, setSharedScanPreview] = useState(null);
  const [showSharedScanModal, setShowSharedScanModal] = useState(false);
  const [scanningShared, setScanningShared] = useState(false);
  
  // Course lookup modal
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  
  // Link player to user modal state
  const [showLinkPlayerModal, setShowLinkPlayerModal] = useState(false);
  const [linkingPlayerId, setLinkingPlayerId] = useState(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  
  // Sync scores to history state
  const [syncingScores, setSyncingScores] = useState(false);
  const [scoresSynced, setScoresSynced] = useState(false);
  
  // Handicap strokes toggle state (local view only - doesn't change saved data)
  const [showHandicapStrokes, setShowHandicapStrokes] = useState(true);
  
  // Editing score after finalization
  const [editingScorePlayer, setEditingScorePlayer] = useState(null);
  const [editScores, setEditScores] = useState([]);
  
  // Track if settings have been modified after results were calculated
  const [needsRecalculate, setNeedsRecalculate] = useState(false);
  
  // Editing game settings
  const [editingSkinValue, setEditingSkinValue] = useState(false);
  const [tempSkinValue, setTempSkinValue] = useState('');
  
  // Prize Pool Setup Modal state (legacy - kept for compatibility)
  const [showPrizePoolSetup, setShowPrizePoolSetup] = useState(false);
  const [prizePoolConfig, setPrizePoolConfig] = useState({
    hasPrizePool: null,  // null = not yet decided, true/false = decision made
    entryFeeType: 'wallet',  // 'wallet' or 'coins'
    entryFee: 20,
    estimatedPlayers: 4
  });
  
  // New Setup Carousel state
  const [showSetupCarousel, setShowSetupCarousel] = useState(false);
  const [reopenCarouselAfterAction, setReopenCarouselAfterAction] = useState(false);
  
  // Track which hole was last completed by all players (for showing hole info card)
  const [lastCompletedHoleByAll, setLastCompletedHoleByAll] = useState(null);
  // Track holes where info card has been shown to avoid re-showing
  const [holesWithInfoShown, setHolesWithInfoShown] = useState(new Set());
  
  // Show initial course card when game just started (active status)
  const [showInitialCourseCard, setShowInitialCourseCard] = useState(true);
  // Manual hole info card trigger (user clicked the info button)
  const [showManualHoleInfo, setShowManualHoleInfo] = useState(false);
  const [manualHoleInfoNumber, setManualHoleInfoNumber] = useState(1);
  
  // Reset when event status changes to active
  useEffect(() => {
    if (event?.status === 'active') {
      setShowInitialCourseCard(true);
    }
  }, [event?.status]);
  
  
  // Use refs to track current state for polling
  const isEditingRef = useRef(false);
  const playerScoresRef = useRef({});
  
  // Check for addPlayers query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('addPlayers') === 'true') {
      setShowAddPlayerModal(true);
      // Clean up URL
      params.delete('addPlayers');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
  
  // Keep refs in sync with state
  useEffect(() => {
    // Check if any player has scores entered
    const hasAnyScores = Object.values(playerScores).some(
      scores => scores && scores.some(s => s !== '' && s !== null && s !== undefined)
    );
    isEditingRef.current = hasAnyScores;
    playerScoresRef.current = playerScores;
  }, [playerScores]);

  const fetchEvent = async (resetScores = false) => {
    const sequence = ++fetchSequence.current;
    try {
      const response = await axios.get(`${API}/skins/events/${eventId}`, { timeout: 20000 });
      if (!mounted.current || sequence !== fetchSequence.current) return false;
      if (response.data?.event_id !== eventId || !Array.isArray(response.data.players)) throw new Error('Invalid event response');
      setEvent(response.data);
      currentEventRef.current = response.data;
      setLoadError('');
      
      // Set active round for RonRo yardage mode when game is in progress
      if (response.data.status === 'active' && response.data.course_id) {
        setActiveRoundForYardage({
          course_id: response.data.course_id,
          course_name: response.data.course_name,
          game_type: 'skins',
          event_id: eventId,
          current_hole: 1
        });
      } else if (response.data.status === 'completed') {
        clearActiveRound();
      }
      
      // Initialize scores for players without scorecards
      if (resetScores && response.data.players) {
        const initialScores = {};
        response.data.players.forEach(p => {
          const hasScorecard = (response.data.scorecards || []).some(sc => sc.player_id === p.player_id);
          if (!hasScorecard) {
            initialScores[p.player_id] = Array(response.data.holes || 18).fill('');
          }
        });
        setPlayerScores(initialScores);
      }
      return true;
    } catch (error) {
      if (mounted.current && sequence === fetchSequence.current) setLoadError(apiError(error, 'Unable to refresh this game. Your saved progress has not been removed.'));
      return false;
    } finally {
      if (mounted.current && sequence === fetchSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    fetchEvent(true); // Initial load - reset scores
    
    // Poll for updates - but COMPLETELY STOP when user is editing
    const interval = setInterval(() => {
      // NEVER poll if user has any scores entered
      if (isEditingRef.current || currentEventRef.current?.status !== 'active') {
        return;
      }
      fetchEvent(false);
    }, 10000);
    
    return () => { mounted.current = false; fetchSequence.current += 1; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]); // fetchEvent is stable, uses eventId internally

  // Load draft scores for all incomplete players when event loads
  useEffect(() => {
    let cancelled = false;
    const loadAllDrafts = async () => {
      if (!event || !event.players) return;
      setDraftState('loading');
      const newScores = {};
      let failed = false;
      for (const player of event.players) {
        const hasScorecard = (event.scorecards || []).some(sc => sc.player_id === player.player_id);
        if (hasScorecard) continue;
        if (!event.is_creator && player.user_id !== user?.user_id) continue;
        
        try {
          const response = await axios.get(`${API}/skins/events/${eventId}/scorecards/draft/${player.player_id}`);
          if (response.data.is_draft && response.data.hole_scores.length > 0) {
            const scores = response.data.hole_scores.map(s => s === null ? '' : String(s));
            newScores[player.player_id] = scores;
          }
        } catch (error) {
          if (error.response?.status !== 404) failed = true;
        }
      }
      if (cancelled) return;
      if (Object.values(newScores).some(scores => scores.some(score => score !== '')) || (event.scorecards || []).length) {
        setShowInitialCourseCard(false);
      }
      setPlayerScores(current => {
        const merged = { ...current };
        Object.entries(newScores).forEach(([id, scores]) => {
          merged[id] = scores.map((score, index) => current[id]?.[index] || score);
        });
        return merged;
      });
      setDraftState(failed ? 'error' : 'ready');
    };
    
    if (event && (event.status === 'active' || activeTab === 'scorecards') && event.status !== 'setup') {
      loadAllDrafts();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.event_id, event?.status, activeTab, draftRetry]);

  const removePlayer = async (playerId) => {
    if (!confirm("Remove this player?")) return;
    try {
      await axios.delete(`${API}/skins/events/${eventId}/players/${playerId}`);
      toast.success("Player removed");
      fetchEvent();
    } catch (error) {
      toast.error("Failed to remove player");
    }
  };

  // Update player handicap
  const updatePlayerHandicap = async (playerId, handicap) => {
    try {
      await axios.patch(`${API}/skins/events/${eventId}/players/${playerId}`, {
        handicap: parseOptionalHandicap(handicap)
      });
      toast.success("Handicap updated");
      setEditingHandicapPlayer(null);
      setNewHandicapValue('');
      fetchEvent();
    } catch (error) {
      toast.error("Failed to update handicap");
    }
  };

  // Search for users to link to guest player
  const searchUsersForLink = async (query) => {
    if (query.length < 2) {
      setLinkSearchResults([]);
      return;
    }
    setLinkSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
      setLinkSearchResults(response.data.users || []);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLinkSearching(false);
    }
  };

  // Link a guest player to a Par Seeker user account
  const linkPlayerToUser = async (playerId, targetUser) => {
    try {
      await axios.patch(`${API}/skins/events/${eventId}/players/${playerId}`, {
        user_id: targetUser.user_id
      });
      toast.success(`Linked ${targetUser.name} to this player! They can now view this game in their dashboard.`);
      setShowLinkPlayerModal(false);
      setLinkingPlayerId(null);
      setLinkSearchQuery('');
      setLinkSearchResults([]);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link player");
    }
  };

  // Sync scores from completed game to Recent Rounds/History
  const syncScoresToHistory = async () => {
    setSyncingScores(true);
    try {
      const response = await axios.post(`${API}/skins/events/${eventId}/sync-scores`);
      if (response.data.scores_synced > 0) {
        toast.success(`Synced ${response.data.scores_synced} score(s) to your Recent Rounds!`);
        setScoresSynced(true);
      } else {
        toast.info("Scores already synced or no linked players with scorecards");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync scores");
    } finally {
      setSyncingScores(false);
    }
  };

  // Handle Start Game - show Setup Carousel
  const handleStartGameClick = () => {
    // Show the swipeable setup carousel instead of the old modal
    setShowSetupCarousel(true);
  };
  
  // Update event settings from carousel
  const handleCarouselUpdateEvent = async (updates) => {
    try {
      await axios.patch(`${API}/skins/events/${eventId}`, updates, { timeout: 20000 });
      setEvent(prev => ({ ...prev, ...updates }));
      await fetchEvent(false);
    } catch (error) {
      toast.error("Failed to update settings");
      throw error;
    }
  };
  
  // Remove player from event
  const handleRemovePlayer = async (playerId) => {
    try {
      await axios.delete(`${API}/skins/events/${eventId}/players/${playerId}`);
      toast.success("Player removed");
      fetchEvent(false);
    } catch (error) {
      toast.error("Failed to remove player");
    }
  };
  
  // Start game from carousel - just starts the event (settings already saved)
  const handleCarouselStartGame = async () => {
    try {
      // Clear any existing draft scores for this event first
      for (const player of (event?.players || [])) {
        try {
          await axios.delete(`${API}/skins/events/${eventId}/scorecards/draft/${player.player_id}`, { timeout: 15000 });
        } catch (e) {
          // Ignore - draft may not exist
        }
      }
      
      // Reset local score state to fresh empty arrays
      const freshScores = {};
      (event?.players || []).forEach(p => {
        freshScores[p.player_id] = Array(event?.holes || 18).fill('');
      });
      setPlayerScores(freshScores);
      
      // Start the event
      await axios.post(`${API}/skins/events/${eventId}/start`, {}, { timeout: 20000 });
      toast.success("Skins game started!");
      
      // Set active round in localStorage for RonRo yardage feature
      localStorage.setItem('par_seeker_active_round', JSON.stringify({
        type: 'skins',
        event_id: eventId,
        course_id: event?.course_id,
        course_name: event?.course_name,
        holes: event?.holes || 18,
        current_hole: 1,
        started_at: new Date().toISOString()
      }));
      
      fetchEvent(true);
    } catch (error) {
      toast.error(apiError(error, 'Failed to start event'));
      throw error;
    }
  };
  
  // Check if all players have completed a specific hole
  const checkAllPlayersCompletedHole = (holeNumber, updatedPlayerScores = null) => {
    if (!event || !event.players || event.players.length < 2) return false;
    
    const players = event.players || [];
    const scoresToCheck = updatedPlayerScores || playerScores;
    let completedCount = 0;
    
    // Get the hole index (0-indexed)
    const holeIndex = (event.starting_nine === 'back' && event.holes === 9) 
      ? holeNumber - 10 
      : holeNumber - 1;
    
    players.forEach(player => {
      // First check if player has a finalized scorecard
      const finalized = (event.scorecards || []).find(sc => sc.player_id === player.player_id);
      if (finalized && finalized.hole_scores && finalized.hole_scores[holeIndex] !== undefined && finalized.hole_scores[holeIndex] !== null) {
        completedCount++;
        return;
      }
      
      // Otherwise check current editing scores from state
      const currentScores = scoresToCheck[player.player_id];
      if (currentScores && currentScores[holeIndex] !== undefined && currentScores[holeIndex] !== '' && currentScores[holeIndex] !== null) {
        completedCount++;
      }
    });
    
    return completedCount === players.length;
  };
  
  // Handle when a player's score is saved - check if all players completed that hole
  const handlePlayerScoreSaved = useCallback((holeNumber) => {
    // Only relevant for multi-player games
    if (!event || !event.players || event.players.length < 2) return;
    
    // Use slightly longer delay to ensure state has updated
    setTimeout(() => {
      // Get latest scores from ref which is always current
      const currentScores = playerScoresRef.current;
      
      if (checkAllPlayersCompletedHole(holeNumber, currentScores) && !holesWithInfoShown.has(holeNumber)) {
        // All players have completed this hole - show info card for NEXT hole
        const nextHole = holeNumber + 1;
        const maxHole = (event.starting_nine === 'back' && event.holes === 9) ? 18 : event.holes || 18;
        
        if (nextHole <= maxHole) {
          setLastCompletedHoleByAll(nextHole);
          setHolesWithInfoShown(prev => new Set([...prev, holeNumber]));
        }
      }
    }, 500); // Increased delay for state propagation
  }, [event, holesWithInfoShown]);

  // Get the current hole being played (first hole without a score for most players)
  const getCurrentHole = useCallback(() => {
    if (!event || !event.players || event.players.length === 0) return 1;
    
    const numHoles = event.holes || 18;
    const startHole = (event.starting_nine === 'back' && event.holes === 9) ? 10 : 1;
    
    // Find the first hole where not all players have entered a score
    for (let h = 0; h < numHoles; h++) {
      const actualHole = startHole + h;
      let playersWithScore = 0;
      
      for (const player of event.players) {
        const scores = playerScores[player.player_id] || [];
        if (scores[h] !== undefined && scores[h] !== '' && scores[h] !== null) {
          playersWithScore++;
        }
      }
      
      // If not all players have scored this hole, this is the current hole
      if (playersWithScore < event.players.length) {
        return actualHole;
      }
    }
    
    // All holes complete, return last hole
    return startHole + numHoles - 1;
  }, [event, playerScores]);

  // Show hole info card when user clicks the info button
  const handleShowHoleInfo = () => {
    const currentHole = getCurrentHole();
    setManualHoleInfoNumber(currentHole);
    setShowManualHoleInfo(true);
  };
  
  // Actually start the game after prize pool is configured
  const startEvent = async () => {
    try {
      // First update the event with prize pool settings if enabled
      if (prizePoolConfig.hasPrizePool) {
        await axios.patch(`${API}/skins/events/${eventId}`, {
          has_prize_pool: true,
          entry_fee: prizePoolConfig.entryFee,
          entry_fee_type: prizePoolConfig.entryFeeType,
          estimated_players: prizePoolConfig.estimatedPlayers
        });
      } else {
        await axios.patch(`${API}/skins/events/${eventId}`, {
          has_prize_pool: false,
          entry_fee: 0,
          entry_fee_type: null
        });
      }
      
      // Then start the event
      await axios.post(`${API}/skins/events/${eventId}/start`);
      toast.success("Skins game started!");
      setShowPrizePoolSetup(false);
      
      // Set active round in localStorage for RonRo yardage feature
      localStorage.setItem('par_seeker_active_round', JSON.stringify({
        type: 'skins',
        event_id: eventId,
        course_id: event?.course_id,
        course_name: event?.course_name,
        holes: event?.holes || 18,
        current_hole: 1,
        started_at: new Date().toISOString()
      }));
      
      fetchEvent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start event");
    }
  };

  // Update scores for a specific player
  const updatePlayerScore = (playerId, newScores) => {
    setPlayerScores(prev => ({
      ...prev,
      [playerId]: newScores
    }));
  };

  // Finalize a player's scorecard (scores auto-saved to history when game completes)
  const finalizePlayerScorecard = async (playerId, scores) => {
    const player = (event.players || []).find(p => p.player_id === playerId);
    if (!player) return;
    
    const allFilled = scores.slice(0, event.holes || 18).every(s => s !== '' && s !== null);
    if (!allFilled) {
      toast.error("Please enter all hole scores before finalizing");
      return;
    }
    
    setSubmittingPlayer(playerId);
    try {
      // Submit scorecard to skins event
      await axios.post(`${API}/skins/events/${eventId}/scorecards`, {
        player_id: playerId,
        hole_scores: scores.slice(0, event.holes || 18).map(s => parseInt(s))
      });
      
      // Delete draft after successful submission
      try {
        await axios.delete(`${API}/skins/events/${eventId}/scorecards/draft/${playerId}`);
      } catch (e) {
      }
      
      toast.success("Scorecard finalized!");
      
      // Clear this player's scores from state
      setPlayerScores(prev => {
        const newScores = { ...prev };
        delete newScores[playerId];
        return newScores;
      });
      
      // Fetch updated event and check if all scorecards are submitted
      const updatedEventRes = await axios.get(`${API}/skins/events/${eventId}`);
      const updatedEvent = updatedEventRes.data;
      setEvent(updatedEvent);
      
      // Auto-calculate results when all players have submitted scorecards
      const allPlayers = updatedEvent.players || [];
      const submittedScorecards = updatedEvent.scorecards || [];
      
      if (updatedEvent.is_creator && allPlayers.length >= 2 && allPlayers.every(p => submittedScorecards.some(s => s.player_id === p.player_id)) && !updatedEvent.results) {
        // All scorecards submitted, auto-calculate results
        toast.info("All scorecards in! Calculating results...");
        await calculateResults();
      }
    } catch (error) {
      toast.error("Failed to finalize scorecard");
    } finally {
      setSubmittingPlayer(null);
    }
  };

  const calculateResults = async () => {
    if (calculationLock.current) return;
    calculationLock.current = true;
    setCalculationError('');
    setCalculating(true);
    try {
      const { data: savedResults } = await axios.post(`${API}/skins/events/${eventId}/calculate`, {}, { timeout: 45000 });
      setEvent(current => ({ ...current, status: 'completed', results: savedResults }));
      toast.success("Results calculated!");
      
      // Dispatch event to refresh Golf Coins balance in header
      window.dispatchEvent(new Event('golfCoinsUpdated'));
      
      // Fetch updated event and show winner modal
      const response = await axios.get(`${API}/skins/events/${eventId}`);
      const updatedEvent = response.data;
      setEvent(updatedEvent);
      
      // Find the winner and show celebration
      if (updatedEvent.results?.player_skins) {
        const playerSkins = updatedEvent.results.player_skins;
        const sortedPlayers = Object.entries(playerSkins).sort(([, a], [, b]) => b - a);
        if (sortedPlayers.length > 0 && sortedPlayers[0][1] > 0) {
          const [winnerId, winnerSkins] = sortedPlayers[0];
          const winnerPlayer = updatedEvent.players?.find(p => p.player_id === winnerId);
          
          // Check if using the new prize pool system
          const usesPrizePool = updatedEvent.entry_fee && updatedEvent.entry_fee > 0 && updatedEvent.entry_fee_type;
          const isCoins = updatedEvent.entry_fee_type === 'coins';
          const numPlayers = updatedEvent.players?.length || 2;
          const totalSkinsInGame = Object.values(playerSkins).reduce((sum, s) => sum + s, 0);
          
          let netWinnings, grossWinnings, buyIn;
          
          if (usesPrizePool) {
            // New system: entry_fee is total per player
            const totalPot = updatedEvent.entry_fee * numPlayers;
            const skinWorth = totalSkinsInGame > 0 ? totalPot / totalSkinsInGame : 0;
            grossWinnings = winnerSkins * skinWorth;
            buyIn = updatedEvent.entry_fee;
            netWinnings = grossWinnings - buyIn;
          } else {
            // Old system: skin_value per hole
            const perPlayerPerHole = updatedEvent.skin_value ?? 0;
            const skinWorth = perPlayerPerHole * numPlayers;
            grossWinnings = winnerSkins * skinWorth;
            buyIn = totalSkinsInGame * perPlayerPerHole;
            netWinnings = grossWinnings - buyIn;
          }
          
          setWinnerData({
            name: winnerPlayer?.name || 'Unknown',
            skins: winnerSkins,
            amount: netWinnings,
            grossAmount: grossWinnings,
            buyIn: buyIn,
            playerId: winnerId,
            isCoins: isCoins,
            entryFeeType: updatedEvent.entry_fee_type
          });
          setShowWinnerModal(true);
        }
      }
    } catch (error) {
      const message = apiError(error, 'Unable to finish calculation. Refresh saved results before trying again.');
      setCalculationError(message);
      toast.error(message);
    } finally {
      calculationLock.current = false;
      setCalculating(false);
    }
  };

  // Recalculate results (force recalculation after score edits)
  const recalculateResults = async () => {
    setCalculating(true);
    try {
      // Force recalculation
      await axios.post(`${API}/skins/events/${eventId}/calculate`, { force: true });
      toast.success("Results recalculated!");
      
      // Dispatch event to refresh Golf Coins balance in header
      window.dispatchEvent(new Event('golfCoinsUpdated'));
      
      fetchEvent(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recalculate results");
    } finally {
      setCalculating(false);
    }
  };

  // Update a player's scorecard after finalization
  const updatePlayerScorecard = async (playerId, newScores) => {
    try {
      await axios.put(`${API}/skins/events/${eventId}/scorecards/${playerId}`, {
        hole_scores: newScores.map(s => s === '' ? null : parseInt(s) || null)
      });
      toast.success("Scores updated!");
      setEditingScorePlayer(null);
      setEditScores([]);
      // Mark that recalculation may be needed if game is complete
      if (event?.status === 'complete') {
        setNeedsRecalculate(true);
      }
      fetchEvent(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update scores");
    }
  };

  // Toggle use_handicaps setting on the event
  const toggleUseHandicaps = async () => {
    try {
      const newValue = !event.use_handicaps;
      await axios.patch(`${API}/skins/events/${eventId}`, {
        use_handicaps: newValue
      });
      toast.success(newValue ? "Handicaps enabled for skins calculation" : "Handicaps disabled - raw scores only");
      // Mark that recalculation may be needed if game is complete
      if (event?.status === 'complete') {
        setNeedsRecalculate(true);
      }
      fetchEvent(false);
    } catch (error) {
      toast.error("Failed to update handicap setting");
    }
  };

  // Update skin value
  const updateSkinValue = async (newValue) => {
    try {
      const value = parseOptionalHandicap(newValue);
      if (isNaN(value) || value < 0) {
        toast.error("Please enter a valid amount");
        return;
      }
      await axios.patch(`${API}/skins/events/${eventId}`, {
        skin_value: value
      });
      toast.success(`Skin value updated to $${value.toFixed(2)}`);
      setEditingSkinValue(false);
      // Mark that recalculation may be needed if game is complete
      if (event?.status === 'complete') {
        setNeedsRecalculate(true);
      }
      fetchEvent(false);
    } catch (error) {
      toast.error("Failed to update skin value");
    }
  };

  // Handle course selection from lookup modal
  const handleCourseSelect = async (courseData) => {
    try {
      await axios.patch(`${API}/skins/events/${eventId}`, {
        course_name: courseData.name,
        course_id: courseData.course_id,
        hole_pars: courseData.hole_pars || [],
        hole_handicaps: courseData.hole_handicaps || [],
        tees: courseData.tees || [],
        course_location: courseData.location || '',
        selected_tee: null,
        hole_yardages: []
      }, { timeout: 20000 });
      toast.success(`Course set to ${courseData.name}`);
      await fetchEvent(false);
    } catch (error) {
      throw error;
    }
  };

  // Refresh course data from GolfCourseAPI
  const [refreshingCourse, setRefreshingCourse] = useState(false);
  
  const refreshCourseData = async () => {
    if (!event?.course_name) {
      toast.error('No course selected');
      return;
    }
    
    setRefreshingCourse(true);
    try {
      // Search for the course
      const searchResponse = await axios.post(`${API}/voice/lookup-course`, {
        course_name: event.course_name,
        location: event.course_location || ''
      });
      
      if (searchResponse.data.success) {
        if (searchResponse.data.multiple_results) {
          // Multiple results - open course lookup modal to let user select
          toast.info('Multiple courses found - please select the correct one');
          setShowCourseLookup(true);
        } else if (searchResponse.data.course) {
          // Single result - update directly
          const courseData = searchResponse.data.course;
          await axios.patch(`${API}/skins/events/${eventId}`, {
            course_name: courseData.name,
            hole_pars: courseData.hole_pars || [],
            hole_handicaps: courseData.hole_handicaps || []
          });
          toast.success(`Course data refreshed from ${searchResponse.data.source === 'golfcourseapi' ? 'GolfCourseAPI' : 'database'}`);
          fetchEvent(false);
        }
      } else {
        toast.error(searchResponse.data.message || 'Course not found in database');
      }
    } catch (error) {
      toast.error('Failed to refresh course data');
    } finally {
      setRefreshingCourse(false);
    }
  };

  // Calculate payout breakdown - simplified: who owes the winner
  const calculatePayouts = () => {
    if (!event?.results?.player_skins || !event?.players) return null;
    if (event.results.payouts_status === 'held_for_eligibility_review' || (event.use_handicaps && event.players.some(p => !hasHandicap(eventHandicap(p))))) return null;
    
    const playerSkins = event.results.player_skins;
    const totalSkins = Object.values(playerSkins).reduce((sum, s) => sum + s, 0);
    const numPlayers = event.players.length;
    
    // Check if using the new prize pool system (entry_fee with entry_fee_type)
    const usesPrizePool = event.entry_fee && event.entry_fee > 0 && event.entry_fee_type;
    const isCoins = event.entry_fee_type === 'coins';
    
    let totalPot, skinWorth, perPlayerContribution;
    
    if (usesPrizePool) {
      // New system: entry_fee is per player
      totalPot = event.entry_fee * numPlayers;
      skinWorth = totalSkins > 0 ? totalPot / totalSkins : 0;
      perPlayerContribution = event.entry_fee;
    } else {
      // Old system: skin_value per hole
      const perPlayerPerHole = event.skin_value ?? 0;
      skinWorth = perPlayerPerHole * numPlayers;
      totalPot = totalSkins * skinWorth;
      perPlayerContribution = totalSkins * perPlayerPerHole;
    }
    
    // Calculate each player's position
    const playerData = event.players.map(p => {
      const skinsWon = playerSkins[p.player_id] || 0;
      const winnings = skinsWon * skinWorth; // What they won
      const amountOwed = perPlayerContribution - winnings; // Positive = owes money, Negative = is owed money
      return {
        playerId: p.player_id,
        name: p.name,
        skinsWon,
        winnings,
        potContribution: perPlayerContribution,
        amountOwed: parseFloat(amountOwed.toFixed(2))
      };
    }).sort((a, b) => a.amountOwed - b.amountOwed); // Sort by who is owed most first
    
    // Find the main winner (player who is owed the most)
    const winner = playerData.find(p => p.amountOwed < 0);
    
    // Players who owe money (positive amountOwed)
    const playersWhoOwe = playerData.filter(p => p.amountOwed > 0);
    
    // Winners (negative amountOwed = they collect)
    const winners = playerData.filter(p => p.amountOwed < 0);
    
    // Calculate detailed payment transactions (who pays whom)
    const transactions = [];
    const winnersRemaining = winners.map(w => ({ ...w, remaining: Math.abs(w.amountOwed) }));
    const owersRemaining = playersWhoOwe.map(o => ({ ...o, remaining: o.amountOwed }));
    
    let wIdx = 0;
    let oIdx = 0;
    while (wIdx < winnersRemaining.length && oIdx < owersRemaining.length) {
      const winner = winnersRemaining[wIdx];
      const ower = owersRemaining[oIdx];
      const amount = Math.min(winner.remaining, ower.remaining);
      
      if (amount > 0.01) {
        transactions.push({
          fromId: ower.playerId,
          fromName: ower.name,
          toId: winner.playerId,
          toName: winner.name,
          amount: parseFloat(amount.toFixed(2))
        });
      }
      
      winner.remaining -= amount;
      ower.remaining -= amount;
      
      if (winner.remaining < 0.01) wIdx++;
      if (ower.remaining < 0.01) oIdx++;
    }
    
    // Build payment details per player
    const paymentDetails = {};
    playerData.forEach(p => {
      paymentDetails[p.playerId] = {
        pays: transactions.filter(t => t.fromId === p.playerId).map(t => ({ to: t.toName, toId: t.toId, amount: t.amount })),
        receives: transactions.filter(t => t.toId === p.playerId).map(t => ({ from: t.fromName, fromId: t.fromId, amount: t.amount }))
      };
    });
    
    return {
      playerData,
      winner,
      playersWhoOwe,
      winners,
      totalPot,
      perPlayerContribution,
      skinWorth,
      usesPrizePool,
      isCoins,
      entryFeeType: event.entry_fee_type,
      transactions,
      paymentDetails
    };
  };

  // Shared scan functionality - scan once, apply to all players
  const handleSharedScan = async (file) => {
    if (!file) return;
    setSharedScanPreview(URL.createObjectURL(file));
    setScanningShared(true);
    setSharedScanResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/scorecard/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const players = response.data.players || [];
        const playerCount = response.data.player_count || 1;
        
        if (playerCount > 1 && players.length > 1) {
          setSharedScanResult({
            ...response.data,
            players: players
          });
          toast.success(`Found ${playerCount} players! Match each to your game players.`);
        } else {
          // Single player detected - still useful
          const singlePlayerScores = response.data.holes?.map(h => h.score) || 
            (players[0]?.hole_scores || []);
          setSharedScanResult({
            ...response.data,
            players: [{
              player_name: "Scanned Player",
              hole_scores: singlePlayerScores,
              total_score: response.data.total_score
            }]
          });
          toast.success("Scorecard scanned! Apply scores to players.");
        }
      } else {
        toast.error(response.data.message || "Could not analyze scorecard");
        setSharedScanResult(null);
      }
    } catch (error) {
      toast.error("Failed to scan scorecard");
      setSharedScanResult(null);
    } finally {
      setScanningShared(false);
    }
  };

  // Apply scanned scores to a specific event player
  const applyScannedScoresToPlayer = (scannedPlayerIndex, eventPlayerId) => {
    if (!sharedScanResult?.players?.[scannedPlayerIndex]) return;
    
    const scannedPlayer = sharedScanResult.players[scannedPlayerIndex];
    const scores = (scannedPlayer.hole_scores || []).slice(0, event.holes || 18);
    
    // Pad to required holes
    while (scores.length < (event.holes || 18)) scores.push(null);
    
    // Convert to strings for the form
    const stringScores = scores.map(s => s !== null && s !== undefined ? String(s) : '');
    
    setPlayerScores(prev => ({
      ...prev,
      [eventPlayerId]: stringScores
    }));
    
    // Auto-save draft
    saveDraft(eventPlayerId, stringScores);
    
    const playerName = event.players?.find(p => p.player_id === eventPlayerId)?.name || 'Player';
    const scannedName = scannedPlayer.player_name || `Scanned Player ${scannedPlayerIndex + 1}`;
    toast.success(`Applied ${scannedName}'s scores to ${playerName}`);
  };

  // Clear shared scan
  const clearSharedScan = () => {
    setSharedScanResult(null);
    setSharedScanPreview(null);
  };

  // Save draft helper
  const saveDraft = async (playerId, scores) => {
    try {
      await axios.put(`${API}/skins/events/${eventId}/scorecards/draft`, {
        player_id: playerId,
        hole_scores: scores.map(s => s === '' ? null : parseInt(s) || null)
      });
    } catch (error) {
    }
  };

  const deleteEvent = async () => {
    if (!confirm("Delete this skins game? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/skins/events/${eventId}`);
      toast.success("Event deleted");
      onBack();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-10 h-10 border-amber-600 border-t-transparent" />
      </div>
    );
  }

  if (!event) return <div className="p-6 space-y-4" data-testid="skins-load-recovery">
    <p role="alert" data-testid="skins-load-error">{loadError || 'Game information is unavailable.'}</p>
    <Button data-testid="skins-load-retry" onClick={() => fetchEvent(true)}>Try again</Button>
    <Button data-testid="skins-load-back" variant="outline" onClick={onBack}>Back to games</Button>
  </div>;

  if (event.legacy_read_only && event.status !== 'completed') return <div className="ps-theme-page p-6 space-y-4" data-testid="skins-legacy-event">
    <Button data-testid="skins-legacy-back" onClick={onBack}>Back to games</Button>
    <h1 className="text-xl font-bold" data-testid="skins-legacy-name">{event.name}</h1>
    <p role="status" data-testid="skins-legacy-notice">This older game is read-only. Its original scores and payments have not been changed.</p>
    <p data-testid="skins-legacy-course">{event.course_name} · {event.date}</p>
    <ul>{event.players.map((p, index) => <li key={index} data-testid={`skins-legacy-player-${index}`}>{p.name}</li>)}</ul>
  </div>;

  const playerMap = {};
  (event.players || []).forEach(p => { playerMap[p.player_id] = p; });

  const getPlayerScorecard = (playerId) => {
    return (event.scorecards || []).find(sc => sc.player_id === playerId);
  };

  // Show Setup Carousel when event is in setup status and user is creator
  if (event.status === 'setup' && event.is_creator) {
    return (
      <div className="max-w-4xl mx-auto animate-fade-in pt-2 px-4" data-testid="skins-event-detail">
        {/* Course Lookup Modal */}
        <CourseLookupModal
          isOpen={showCourseLookup}
          onClose={() => setShowCourseLookup(false)}
          onCourseSelect={async (courseData) => {
            await handleCourseSelect(courseData);
          }}
          currentCourseName={event.course_name}
          title="Find Course for Skins Game"
          returnFocusId="skins-setup-carousel"
        />

        {/* Add Player Modal */}
        {showAddPlayerModal && (
          <AddPlayerModal 
            eventId={eventId}
            eventType="skins"
            onClose={() => setShowAddPlayerModal(false)}
            onAdded={async () => {
              setShowAddPlayerModal(false);
              await fetchEvent();
            }}
          />
        )}

        {/* Full-page Setup Carousel */}
        <SkinsSetupCarousel
          event={event}
          loadError={loadError}
          onRetry={() => fetchEvent(false)}
          players={event.players || []}
          onBack={onBack}
          onExit={onBack}
          suspended={showCourseLookup || showAddPlayerModal}
          onUpdateEvent={handleCarouselUpdateEvent}
          onAddPlayer={() => setShowAddPlayerModal(true)}
          onRemovePlayer={handleRemovePlayer}
          onStartGame={handleCarouselStartGame}
          onOpenCourseLookup={() => setShowCourseLookup(true)}
        />
      </div>
    );
  }

  // For ACTIVE games: Show clean scorecard-focused view
  if (event.status === 'active') {
    return (
      <div className="max-w-4xl mx-auto animate-fade-in pt-2 px-4" data-testid="skins-event-detail">
        {/* Initial Course Card - Shows when game just started */}
        {draftState === 'ready' && showInitialCourseCard && event.course_name && (
          <SharedHoleInfoCard
            courseId={event.course_id}
            holeNumber={event.starting_nine === 'back' && event.holes === 9 ? 10 : 1}
            par={(event.hole_pars || [])[event.starting_nine === 'back' ? 9 : 0]}
            yardage={(event.hole_yardages || [])[event.starting_nine === 'back' ? 9 : 0]}
            handicap={(event.hole_handicaps || [])[event.starting_nine === 'back' ? 9 : 0]}
            courseName={event.course_name}
            teeName={event.selected_tee}
            isVisible={true}
            onDismiss={() => setShowInitialCourseCard(false)}
          />
        )}

        {/* Shared Hole Info Card for multi-player games */}
        {!showInitialCourseCard && (event.players || []).length > 1 && lastCompletedHoleByAll && (
          <SharedHoleInfoCard
            courseId={event.course_id}
            holeNumber={lastCompletedHoleByAll}
            par={(event.hole_pars || [])[lastCompletedHoleByAll - 1]}
            yardage={(event.hole_yardages || [])[lastCompletedHoleByAll - 1]}
            handicap={(event.hole_handicaps || [])[lastCompletedHoleByAll - 1]}
            courseName={event.course_name}
            teeName={event.selected_tee}
            isVisible={true}
            onDismiss={() => setLastCompletedHoleByAll(null)}
          />
        )}

        {/* Manual Hole Info Card - triggered by user clicking info button */}
        {showManualHoleInfo && event.course_name && (
          <SharedHoleInfoCard
            courseId={event.course_id}
            holeNumber={manualHoleInfoNumber}
            par={(event.hole_pars || [])[
              (event.starting_nine === 'back' && event.holes === 9) 
                ? manualHoleInfoNumber - 10 
                : manualHoleInfoNumber - 1
            ]}
            yardage={(event.hole_yardages || [])[
              (event.starting_nine === 'back' && event.holes === 9) 
                ? manualHoleInfoNumber - 10 
                : manualHoleInfoNumber - 1
            ]}
            handicap={(event.hole_handicaps || [])[
              (event.starting_nine === 'back' && event.holes === 9) 
                ? manualHoleInfoNumber - 10 
                : manualHoleInfoNumber - 1
            ]}
            courseName={event.course_name}
            teeName={event.selected_tee}
            isVisible={true}
            onDismiss={() => setShowManualHoleInfo(false)}
          />
        )}

        {/* Compact Header - Back button, Hole Info button, and status */}
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" onClick={onBack} className="p-2 -ml-2" data-testid="skins-active-back" aria-label="Back to games">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          {/* Hole Info Quick Access Button */}
          {event.course_name && (
            <button
              onClick={handleShowHoleInfo}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-900/40 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-900/60 transition-all"
              data-testid="hole-info-btn"
            >
              <Flag className="w-4 h-4" />
              <span className="text-sm font-medium">Hole {getCurrentHole()}</span>
            </button>
          )}
          
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            In Progress
          </span>
        </div>

        {draftState === 'loading' && <p role="status" data-testid="skins-drafts-loading" className="mb-3 text-sm">Loading saved scores…</p>}
        {draftState === 'error' && <div role="alert" data-testid="skins-drafts-error" className="mb-3 text-sm">
          Saved scores could not be loaded. Score entry is paused to protect your progress.
          <Button variant="outline" data-testid="skins-drafts-retry" onClick={() => setDraftRetry(value => value + 1)}>Retry saved scores</Button>
        </div>}
        {/* Scorecard Section - Direct display without tabs */}
        <div className={`rounded-2xl overflow-hidden ${isDark ? '' : 'bg-white border border-stone-200'}`} style={isDark ? {
          background: 'linear-gradient(145deg, #1a1a1a 0%, #0d0d0d 100%)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 -1px 2px rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)'
        } : {}}>
          {/* Player Scorecards */}
          {(event.players || []).map((player) => {
            const scorecard = getPlayerScorecard(player.player_id);
            const currentScores = playerScores[player.player_id] || Array(event.holes || 18).fill('');
            const isLinkedUser = player.user_id && player.user_id === user?.user_id;
            const canEdit = event.is_creator || isLinkedUser;
            const isSubmitting = submittingPlayer === player.player_id;
            
            return (
              <div key={player.player_id} className="border-b border-[var(--ps-border)] last:border-b-0">
                {/* Player Header */}
                <div className={`p-4 flex items-center justify-between ${
                  isDark ? 'bg-gradient-to-r from-amber-900/20 to-transparent' : 'bg-gradient-to-r from-amber-50 to-transparent'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      player.user_id ? 'bg-amber-500/30' : 'bg-white/20'
                    }`}>
                      {player.user_id ? (
                        <UserCircle className="w-5 h-5 text-amber-300" />
                      ) : (
                        <User className="w-5 h-5 text-gray-300" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-[var(--ps-text-primary)]">{player.name}</p>
                      {player.handicap !== null && player.handicap !== undefined && (
                        <p className="text-xs text-[var(--ps-text-muted)]">HCP {player.handicap}</p>
                      )}
                    </div>
                  </div>
                  {scorecard && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[var(--ps-text-primary)]">
                        {scorecard.hole_scores?.reduce((a, b) => a + (b || 0), 0) || '-'}
                      </p>
                      <p className="text-xs text-green-500">Finalized</p>
                    </div>
                  )}
                </div>

                {/* Score Entry Form - for unfinalized cards */}
                {!scorecard && canEdit && (
                  <div className={`p-4 ${!isDark ? 'bg-white border-t border-amber-100' : ''}`} style={isDark ? {
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 100%)'
                  } : {}}>
                    <ScoreEntryForm
                      courseId={event.course_id}
                      eventHoles={event.holes || 18}
                      initialScores={currentScores}
                      onScoresChange={(newScores) => updatePlayerScore(player.player_id, newScores)}
                      playerName={player.name}
                      showHolesSelector={false}
                      showVoiceOption={true}
                      showScanOption={true}
                      autoSave={true}
                      autoSaveEndpoint={`${API}/skins/events/${eventId}/scorecards/draft`}
                      autoSavePayload={(scores) => ({
                        player_id: player.player_id,
                        hole_scores: scores.map(s => s === '' ? null : parseInt(s) || null)
                      })}
                      submitButtonText="Finalize Score"
                      showTotalDisplay={true}
                      onSave={({ scores }) => {
                        finalizePlayerScorecard(player.player_id, scores);
                      }}
                      disabled={isSubmitting || draftState !== 'ready'}
                      startingNine={event.starting_nine || 'front'}
                      getActualHoleNumber={(idx) => event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1}
                      holePars={event.hole_pars || []}
                      holeYardages={event.hole_yardages || []}
                      showYardages={event.hole_yardages && event.hole_yardages.length > 0}
                      darkMode={isDark}
                      isMultiPlayer={(event.players || []).length > 1}
                      onAllPlayersCompletedHole={handlePlayerScoreSaved}
                      showHoleInfoCards={draftState === 'ready' && (event.players || []).length <= 1}
                    />
                  </div>
                )}

                {/* Finalized Scorecard Display */}
                {scorecard && (
                  <div className="p-4">
                    <div className="grid grid-cols-9 gap-1 text-center text-sm">
                      {scorecard.hole_scores?.slice(0, event.holes || 18).map((score, idx) => {
                        const actualHole = event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1;
                        const holePar = (event.hole_pars || [])[actualHole - 1];
                        const { scoreType } = getScoreStyle(score, holePar);
                        return (
                          <div key={idx} className="flex flex-col items-center">
                            <span className="text-xs text-[var(--ps-text-muted)]">{actualHole}</span>
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                              scoreType === 'eagle' ? 'bg-yellow-400 text-yellow-900' :
                              scoreType === 'birdie' ? 'bg-green-500 text-white' :
                              scoreType === 'par' ? 'bg-blue-500 text-white' :
                              scoreType === 'bogey' ? 'bg-red-400 text-white' :
                              scoreType === 'double' ? 'bg-red-600 text-white' :
                              'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)]'
                            }`}>
                              {score || '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Finalize Game Button - Shows when all scorecards are in */}
        {calculationError && <p role="alert" data-testid="skins-calculation-error" className="mt-4 text-sm text-red-600">{calculationError}</p>}
        {event.is_creator && (event.players || []).length >= 2 && event.players.every(p => (event.scorecards || []).some(sc => sc.player_id === p.player_id)) && (
          <div className="mt-6">
            <Button
              onClick={calculateResults}
              disabled={submittingPlayer !== null || calculating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4"
              data-testid="finalize-game-btn"
            >
              {calculating ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Calculating...
                </div>
              ) : (
                <>
                  <Trophy className="w-5 h-5 mr-2" />
                  Calculate Skins & Finalize Game
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Completed games use the canonical result returned by the calculation endpoint.
  if (event.status === 'completed') {
    return <SkinsCompletedResults event={event} onBack={onBack} onRefresh={() => fetchEvent(false)}
      onCalculate={calculateResults} calculating={calculating} calculationError={calculationError} refreshError={loadError} />;
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pt-2" data-testid="skins-event-detail">
      <NetEligibilityNotice event={event} />
      {/* Shared Hole Info Card for multi-player games */}
      {(event.players || []).length > 1 && lastCompletedHoleByAll && (
        <SharedHoleInfoCard
          courseId={event.course_id}
          holeNumber={lastCompletedHoleByAll}
          par={(event.hole_pars || [])[lastCompletedHoleByAll - 1]}
          yardage={(event.hole_yardages || [])[lastCompletedHoleByAll - 1]}
          handicap={(event.hole_handicaps || [])[lastCompletedHoleByAll - 1]}
          courseName={event.course_name}
          teeName={event.selected_tee}
          isVisible={true}
          onDismiss={() => setLastCompletedHoleByAll(null)}
        />
      )}
      
      {/* Clean Header - Only Game Name */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" onClick={onBack} className="p-2 -ml-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent flex-1 uppercase tracking-wide">{event.name}</h1>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          event.status === 'active' ? 'bg-amber-100 text-amber-700' :
          event.status === 'completed' ? 'bg-green-100 text-green-700' :
          'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]'
        }`}>
          {event.status === 'active' ? 'In Progress' : event.status === 'completed' ? 'Completed' : 'Setup'}
        </span>
      </div>
      
      {/* Tournament Entry Fee - Join Button for Non-Players */}
      {event.entry_fee > 0 && !event.is_creator && event.status === 'setup' && 
       !(event.players || []).some(p => p.user_id === user?.user_id) && (
        <div className={`mb-4 p-4 rounded-xl border ${event.entry_fee_type === 'coins' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 dark:from-amber-900/20 dark:to-yellow-900/20 dark:border-amber-700/30' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 dark:from-green-900/20 dark:to-emerald-900/20 dark:border-green-700/30'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <TournamentPrizeInfo 
              entryFee={event.entry_fee} 
              entryFeeType={event.entry_fee_type || 'wallet'}
              playerCount={(event.players || []).length}
              compact={true}
            />
            <JoinTournamentButton
              tournamentId={eventId}
              tournamentType="skins"
              entryFee={event.entry_fee}
              entryFeeType={event.entry_fee_type || 'wallet'}
              tournamentName={event.name}
              onJoined={() => fetchEvent()}
              data-testid="join-skins-btn"
            />
          </div>
        </div>
      )}
      
      {/* Game Info Badge - Shows holes being played */}
      {event.holes === 9 && (
        <div className="mb-4 flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            event.starting_nine === 'back' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {event.starting_nine === 'back' ? 'Back 9 (Holes 10-18)' : 'Front 9 (Holes 1-9)'}
          </span>
        </div>
      )}

      {/* Course Lookup Modal */}
      <CourseLookupModal
        isOpen={showCourseLookup}
        onClose={() => {
          setShowCourseLookup(false);
          // Reopen carousel if it was open before
          if (reopenCarouselAfterAction) {
            setReopenCarouselAfterAction(false);
            setShowSetupCarousel(true);
          }
        }}
        onCourseSelect={async (courseData) => {
          await handleCourseSelect(courseData);
        }}
        currentCourseName={event.course_name}
        title="Find Course for Skins Game"
        returnFocusId="skins-setup-carousel"
      />

      {/* Course Par Display */}
      {event.course_name && event.hole_pars && event.hole_pars.length > 0 && (
        <CourseParDisplay
          selectedCourseData={{
            name: event.course_name,
            location: event.course_location,
            par: event.hole_pars.reduce((sum, p) => sum + (parseInt(p) || 0), 0),
            hole_pars: event.hole_pars,
            hole_handicaps: event.hole_handicaps || []
          }}
          numberOfHoles={event.holes || 18}
          startingNine={event.starting_nine || "front"}
          setStartingNine={() => {}}
          getActualHoleNumber={(idx) => event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 mb-6 bg-[var(--ps-bg-elevated)] rounded-xl p-1 sm:p-1.5 overflow-x-auto">
        <button
          onClick={() => setActiveTab('players')}
          className={`flex-1 min-w-fit py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg text-sm sm:text-base font-bold transition-all whitespace-nowrap ${
            activeTab === 'players' 
              ? 'bg-[var(--ps-bg-surface)] text-amber-600 shadow-sm' 
              : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
          }`}
        >
          Players ({event.players?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('scorecards')}
          className={`flex-1 min-w-fit py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg text-sm sm:text-base font-bold transition-all whitespace-nowrap ${
            activeTab === 'scorecards' 
              ? 'bg-[var(--ps-bg-surface)] text-amber-600 shadow-sm' 
              : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
          }`}
        >
          Scorecard
        </button>
        <button
          onClick={() => setActiveTab('results')}
          className={`flex-1 min-w-fit py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg text-sm sm:text-base font-bold transition-all whitespace-nowrap ${
            activeTab === 'results' 
              ? 'bg-[var(--ps-bg-surface)] text-amber-600 shadow-sm' 
              : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
          }`}
        >
          Results
        </button>
        {event.is_creator && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 min-w-fit py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg text-sm sm:text-base font-bold transition-all whitespace-nowrap ${
              activeTab === 'settings' 
                ? 'bg-[var(--ps-bg-surface)] text-amber-600 shadow-sm' 
                : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
            }`}
          >
            Settings
          </button>
        )}
      </div>

      {/* Players Tab */}
      {activeTab === 'players' && (
        <Card>
          <CardContent className="p-4">
            {event.is_creator && event.status === 'setup' && (
              <div className="mb-4">
                <Button onClick={() => setShowAddPlayerModal(true)} variant="outline" className="w-full">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Player
                </Button>
              </div>
            )}

            {(event.players || []).length === 0 ? (
              <div className="text-center py-8 text-[var(--ps-text-secondary)]">
                <Users className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p>No players added yet</p>
                <p className="text-sm">Add at least 2 players to start the game</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(event.players || []).map((player) => {
                  const scorecard = getPlayerScorecard(player.player_id);
                  const isLinkedUser = !player.is_guest && player.user_id;
                  const isEditing = editingHandicapPlayer === player.player_id;
                  const canEdit = event.is_creator && event.status !== 'completed';
                  const hasHandicap = player.handicap !== null && player.handicap !== undefined;
                  
                  return (
                    <div key={player.player_id} className={`rounded-xl overflow-hidden border ${
                      isLinkedUser ? 'border-amber-200 bg-amber-50/50' : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]'
                    }`}>
                      {/* Main Player Info Row */}
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isLinkedUser ? 'bg-amber-100' : 'bg-[var(--ps-bg-hover)]'
                          }`}>
                            {isLinkedUser ? (
                              <UserCircle className="w-5 h-5 text-amber-600" />
                            ) : (
                              <User className="w-5 h-5 text-[var(--ps-text-secondary)]" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-[var(--ps-text-primary)] truncate">{player.name}</p>
                              {isLinkedUser && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex-shrink-0">Linked</span>
                              )}
                              {player.is_guest && !isLinkedUser && (
                                <span className="text-xs bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)] px-1.5 py-0.5 rounded flex-shrink-0">Guest</span>
                              )}
                            </div>
                            {player.email && <p className="text-xs text-[var(--ps-text-muted)] truncate">{player.email}</p>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Handicap Badge - Always Visible, Tappable */}
                          <button
                            onClick={() => {
                              if (canEdit) {
                                setEditingHandicapPlayer(isEditing ? null : player.player_id);
                                setNewHandicapValue(player.handicap?.toString() || '');
                              }
                            }}
                            disabled={!canEdit}
                            className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all ${
                              hasHandicap 
                                ? 'bg-green-100 text-green-700 border border-green-200' 
                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                            } ${canEdit ? 'cursor-pointer hover:shadow-md active:scale-95' : 'cursor-default'}`}
                            title={canEdit ? "Tap to edit handicap" : ""}
                          >
                            HCP {hasHandicap ? player.handicap : '—'}
                            {canEdit && <Edit className="w-3 h-3 opacity-60" />}
                          </button>
                          
                          {/* Remove Button - Only in Setup */}
                          {event.is_creator && event.status === 'setup' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {/* Link to Par Seeker - only for guests */}
                                {player.is_guest && !player.user_id && (
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setLinkingPlayerId(player.player_id);
                                      setShowLinkPlayerModal(true);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    <Link2 className="w-4 h-4 mr-2 text-blue-600" />
                                    Link to Par Seeker
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => removePlayer(player.player_id)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove Player
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          
                          {/* Score Badge - If submitted */}
                          {scorecard && (
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-medium">
                              {scorecard.total_score}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expandable Edit Section */}
                      {isEditing && canEdit && (
                        <div className="px-3 pb-3 pt-0 border-t border-[var(--ps-border)]">
                          <div className="bg-[var(--ps-bg-surface)] rounded-lg p-3 shadow-inner space-y-3">
                            <p className="text-xs font-medium text-[var(--ps-text-secondary)]">Edit Handicap for {player.name}</p>
                            
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.1"
                                min="-5"
                                max="54"
                                className="flex-1 h-10"
                                placeholder="Enter handicap (e.g., 12.5)"
                                value={newHandicapValue}
                                onChange={(e) => setNewHandicapValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') updatePlayerHandicap(player.player_id, newHandicapValue);
                                  if (e.key === 'Escape') { setEditingHandicapPlayer(null); setNewHandicapValue(''); }
                                }}
                                autoFocus
                              />
                              <Button 
                                size="sm" 
                                className="h-10 bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => updatePlayerHandicap(player.player_id, newHandicapValue)}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                            </div>
                            
                            {/* Reload from Profile Button - For linked users */}
                            {isLinkedUser && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
                                onClick={async () => {
                                  try {
                                    // Use the dedicated handicap endpoint for real-time data
                                    const res = await axios.get(`${API}/users/${player.user_id}/handicap`);
                                    const data = res.data;
                                    
                                    if (data.handicap !== null && data.handicap !== undefined) {
                                      await updatePlayerHandicap(player.player_id, data.handicap.toString());
                                      const source = data.handicap_source === 'manual' ? 'manual setting' : 'calculated from scores';
                                      toast.success(`Loaded handicap ${data.handicap} (${source})`);
                                    } else if (data.rounds_needed_for_handicap > 0) {
                                      toast.info(`Player needs ${data.rounds_needed_for_handicap} more round(s) for calculated handicap`);
                                    } else {
                                      toast.info("No handicap found - player can set one in their profile");
                                    }
                                  } catch (err) {
                                    if (err.response?.status === 404) {
                                      toast.error("Player profile not found");
                                    } else {
                                      toast.error("Could not load handicap from profile");
                                    }
                                  }
                                }}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reload from Player's Profile
                              </Button>
                            )}
                            
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 text-[var(--ps-text-secondary)]"
                                onClick={() => { setEditingHandicapPlayer(null); setNewHandicapValue(''); }}
                              >
                                Cancel
                              </Button>
                              {hasHandicap && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex-1 text-red-500"
                                  onClick={() => updatePlayerHandicap(player.player_id, '')}
                                >
                                  Clear Handicap
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Warning for missing handicap */}
                      {!hasHandicap && !isEditing && (
                        <div className="px-3 pb-2">
                          <p className="text-xs text-amber-600 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                            Handicap needed for stroke allocation - tap HCP to set
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {event.is_creator && event.status === 'setup' && (event.players || []).length >= 2 && (
              <div className="mt-4 pt-4 border-t">
                <Button onClick={handleStartGameClick} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                  <Play className="w-4 h-4 mr-2" />
                  Start Skins Game
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Player Modal */}
      {showAddPlayerModal && (
        <AddPlayerModal 
          eventId={eventId}
          eventType="skins"
          onClose={() => {
            setShowAddPlayerModal(false);
            // Reopen carousel if it was open before
            if (reopenCarouselAfterAction) {
              setReopenCarouselAfterAction(false);
              setShowSetupCarousel(true);
            }
          }}
          onAdded={async () => {
            setShowAddPlayerModal(false);
            await fetchEvent();
            // Reopen carousel if it was open before
            if (reopenCarouselAfterAction) {
              setReopenCarouselAfterAction(false);
              setTimeout(() => setShowSetupCarousel(true), 100);
            }
          }}
        />
      )}

      {/* Skins Setup Carousel - Step-by-step wizard */}
      <SkinsSetupCarousel
        event={event}
        isOpen={showSetupCarousel}
        players={event.players || []}
        onExit={onBack}
        onBack={onBack}
        suspended={showCourseLookup || showAddPlayerModal}
        onClose={() => setShowSetupCarousel(false)}
        onUpdateEvent={handleCarouselUpdateEvent}
        onAddPlayer={() => {
          setShowSetupCarousel(false);
          setReopenCarouselAfterAction(true);
          setShowAddPlayerModal(true);
        }}
        onStartGame={handleCarouselStartGame}
        onOpenCourseLookup={() => {
          setShowSetupCarousel(false);
          setReopenCarouselAfterAction(true);
          setShowCourseLookup(true);
        }}
      />

      {/* Prize Pool Setup Modal - Required before starting game */}
      <Dialog open={showPrizePoolSetup} onOpenChange={setShowPrizePoolSetup}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-600" />
              Prize Pool Setup
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Prize Pool Toggle */}
            <div className="text-center">
              <p className="text-sm font-medium text-[var(--ps-text-primary)] mb-3">Add a Prize Pool?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPrizePoolConfig(prev => ({ ...prev, hasPrizePool: true }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    prizePoolConfig.hasPrizePool === true
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-2 ring-green-200'
                      : 'border-[var(--ps-border)] hover:border-green-300'
                  }`}
                  data-testid="prize-pool-yes"
                >
                  <Trophy className={`w-8 h-8 mx-auto mb-2 ${prizePoolConfig.hasPrizePool === true ? 'text-green-600' : 'text-[var(--ps-text-muted)]'}`} />
                  <span className={`font-semibold ${prizePoolConfig.hasPrizePool === true ? 'text-green-700 dark:text-green-400' : 'text-[var(--ps-text-secondary)]'}`}>Yes</span>
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">Compete for prizes</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPrizePoolConfig(prev => ({ ...prev, hasPrizePool: false }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    prizePoolConfig.hasPrizePool === false
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200'
                      : 'border-[var(--ps-border)] hover:border-blue-300'
                  }`}
                  data-testid="prize-pool-no"
                >
                  <Users className={`w-8 h-8 mx-auto mb-2 ${prizePoolConfig.hasPrizePool === false ? 'text-blue-600' : 'text-[var(--ps-text-muted)]'}`} />
                  <span className={`font-semibold ${prizePoolConfig.hasPrizePool === false ? 'text-blue-700 dark:text-blue-400' : 'text-[var(--ps-text-secondary)]'}`}>No</span>
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">Just for fun</p>
                </button>
              </div>
            </div>

            {/* Prize Pool Settings (only show if Yes selected) */}
            {prizePoolConfig.hasPrizePool === true && (
              <>
                {/* Currency Type Selection */}
                <div>
                  <label className="text-sm font-medium text-[var(--ps-text-primary)] mb-2 block">Entry Fee Currency</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPrizePoolConfig(prev => ({ ...prev, entryFeeType: 'wallet', entryFee: 20 }))}
                      className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                        prizePoolConfig.entryFeeType === 'wallet'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-[var(--ps-border)] hover:border-[var(--ps-border-hover)]'
                      }`}
                      data-testid="currency-wallet"
                    >
                      <Wallet className={`w-5 h-5 ${prizePoolConfig.entryFeeType === 'wallet' ? 'text-green-600' : 'text-[var(--ps-text-muted)]'}`} />
                      <div className="text-left">
                        <span className={`font-medium text-sm ${prizePoolConfig.entryFeeType === 'wallet' ? 'text-green-700 dark:text-green-400' : 'text-[var(--ps-text-secondary)]'}`}>Golf Wallet</span>
                        <p className="text-xs text-[var(--ps-text-muted)]">Real USD</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPrizePoolConfig(prev => ({ ...prev, entryFeeType: 'coins', entryFee: 100 }))}
                      className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                        prizePoolConfig.entryFeeType === 'coins'
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-[var(--ps-border)] hover:border-[var(--ps-border-hover)]'
                      }`}
                      data-testid="currency-coins"
                    >
                      <Coins className={`w-5 h-5 ${prizePoolConfig.entryFeeType === 'coins' ? 'text-amber-600' : 'text-[var(--ps-text-muted)]'}`} />
                      <div className="text-left">
                        <span className={`font-medium text-sm ${prizePoolConfig.entryFeeType === 'coins' ? 'text-amber-700 dark:text-amber-400' : 'text-[var(--ps-text-secondary)]'}`}>Golf Coins</span>
                        <p className="text-xs text-[var(--ps-text-muted)]">Virtual currency</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Entry Fee Amount */}
                <div>
                  <label className="text-sm font-medium text-[var(--ps-text-primary)] mb-2 block">
                    Entry Fee per Player {prizePoolConfig.entryFeeType === 'wallet' ? '($)' : '(Coins)'}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {(prizePoolConfig.entryFeeType === 'wallet' 
                      ? [5, 10, 20, 25, 50, 100, 200, 500]
                      : [50, 100, 250, 500, 1000, 2500, 5000, 10000]
                    ).map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPrizePoolConfig(prev => ({ ...prev, entryFee: val }))}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                          prizePoolConfig.entryFee === val
                            ? prizePoolConfig.entryFeeType === 'wallet' 
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                              : 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                            : 'border-[var(--ps-border)] hover:border-[var(--ps-border-hover)] text-[var(--ps-text-secondary)]'
                        }`}
                        data-testid={`entry-fee-${val}`}
                      >
                        {prizePoolConfig.entryFeeType === 'wallet' ? `$${val}` : val.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Prize Pool Preview */}
                {(() => {
                  const playerCount = (event?.players || []).length || prizePoolConfig.estimatedPlayers;
                  const total = prizePoolConfig.entryFee * playerCount;
                  const platformFee = total * 0.10;
                  const prizePool = total - platformFee;
                  
                  return (
                    <div className={`rounded-xl p-4 ${prizePoolConfig.entryFeeType === 'wallet' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[var(--ps-text-secondary)]">Total Pot ({playerCount} players)</span>
                        <span className="font-bold text-lg text-[var(--ps-text-primary)]">
                          {prizePoolConfig.entryFeeType === 'wallet' ? `$${total}` : `${total.toLocaleString()} coins`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2 text-sm">
                        <span className="text-[var(--ps-text-muted)]">Platform Fee (10%)</span>
                        <span className="text-[var(--ps-text-muted)]">
                          -{prizePoolConfig.entryFeeType === 'wallet' ? `$${platformFee.toFixed(2)}` : `${platformFee.toLocaleString()} coins`}
                        </span>
                      </div>
                      <div className="border-t border-[var(--ps-border)] pt-2 flex items-center justify-between">
                        <span className="font-medium text-[var(--ps-text-primary)]">Prize Pool</span>
                        <span className={`font-bold text-xl ${prizePoolConfig.entryFeeType === 'wallet' ? 'text-green-600' : 'text-amber-600'}`}>
                          {prizePoolConfig.entryFeeType === 'wallet' ? `$${prizePool.toFixed(2)}` : `${prizePool.toLocaleString()} coins`}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Info Note */}
                <p className="text-xs text-[var(--ps-text-muted)] text-center">
                  💡 Players who decline the entry fee can still play for fun but will not share in the prize pool.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button 
              onClick={startEvent} 
              disabled={prizePoolConfig.hasPrizePool === null}
              className={`w-full h-12 text-base font-medium ${
                prizePoolConfig.hasPrizePool 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
              data-testid="confirm-start-game"
            >
              {prizePoolConfig.hasPrizePool ? (
                <>
                  <Trophy className="w-5 h-5 mr-2" />
                  Start Game with Prize Pool
                </>
              ) : prizePoolConfig.hasPrizePool === false ? (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Start Game (No Prize Pool)
                </>
              ) : (
                <>
                  <Settings className="w-5 h-5 mr-2" />
                  Select Prize Pool Option
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowPrizePoolSetup(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Player to Par Seeker Account Modal */}
      <Dialog open={showLinkPlayerModal} onOpenChange={(open) => {
        if (!open) {
          setShowLinkPlayerModal(false);
          setLinkingPlayerId(null);
          setLinkSearchQuery('');
          setLinkSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Link to Par Seeker Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[var(--ps-text-secondary)]">
              Search for a registered Par Seeker user to link this guest player. 
              Once linked, they'll see this game in their dashboard.
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
              <Input
                placeholder="Search by name or email..."
                value={linkSearchQuery}
                onChange={(e) => {
                  setLinkSearchQuery(e.target.value);
                  searchUsersForLink(e.target.value);
                }}
                className="pl-10"
                data-testid="link-user-search"
              />
            </div>
            
            {linkSearching && (
              <div className="text-center py-4">
                <div className="spinner w-6 h-6 border-blue-600 border-t-transparent mx-auto" />
              </div>
            )}
            
            {!linkSearching && linkSearchResults.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {linkSearchResults.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => linkPlayerToUser(linkingPlayerId, u)}
                    className="w-full flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] hover:bg-blue-50 rounded-lg transition-colors text-left"
                    data-testid={`link-user-${u.user_id}`}
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[var(--ps-text-primary)] truncate">{u.name}</p>
                      <p className="text-xs text-[var(--ps-text-muted)] truncate">{u.email}</p>
                    </div>
                    <Link2 className="w-4 h-4 text-blue-600" />
                  </button>
                ))}
              </div>
            )}
            
            {!linkSearching && linkSearchQuery.length >= 2 && linkSearchResults.length === 0 && (
              <div className="text-center py-4 text-[var(--ps-text-secondary)]">
                <p className="text-sm">No users found matching "{linkSearchQuery}"</p>
                <p className="text-xs mt-1">They may need to create a Par Seeker account first</p>
              </div>
            )}
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">Why link accounts?</p>
              <p className="text-xs text-blue-600 mt-1">
                Linked players can see this game in their own Par Seeker dashboard, 
                and their scores can contribute to their handicap calculation.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkPlayerModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scorecards Tab - All cards expanded, scroll through players */}
      {activeTab === 'scorecards' && (
        <div className={`rounded-2xl overflow-hidden ${isDark ? '' : 'bg-white border border-stone-200'}`} style={isDark ? {
          background: 'linear-gradient(145deg, #1a1a1a 0%, #0d0d0d 100%)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 -1px 2px rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)'
        } : {}}>
          <div className="p-4">
            {event.status === 'setup' ? (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p className="text-[var(--ps-text-secondary)] mb-4">Start the game to submit scorecards</p>
                {event.is_creator && (event.players || []).length >= 2 && (
                  <Button onClick={handleStartGameClick} className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Play className="w-4 h-4 mr-2" />
                    Start Skins Game
                  </Button>
                )}
                {event.is_creator && (event.players || []).length < 2 && (
                  <p className="text-sm text-amber-500 mt-2">Add at least 2 players to start the game</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-lg text-[var(--ps-text-primary)]">
                  <Flag className="w-5 h-5 text-amber-500" />
                  Player Scorecards
                </h3>
                
                {/* All player cards - always visible */}
                <div className="space-y-4">
                  {(event.players || []).map((player) => {
                    const scorecard = getPlayerScorecard(player.player_id);
                    const currentScores = playerScores[player.player_id] || Array(event.holes || 18).fill('');
                    const isSubmitting = submittingPlayer === player.player_id;
                    const isLinkedUser = player.user_id && !player.is_guest;
                    
                    return (
                      <div key={player.player_id} className={`rounded-xl overflow-hidden ${!isDark ? (scorecard ? 'border border-green-300' : 'border border-amber-200') : ''}`} style={isDark ? {
                        background: scorecard 
                          ? 'linear-gradient(145deg, rgba(34,197,94,0.15) 0%, rgba(22,163,74,0.1) 100%)'
                          : 'linear-gradient(145deg, #1f1f1f 0%, #141414 100%)',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
                        border: scorecard ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.1)'
                      } : {}}>
                        {/* Player Header */}
                        <div className={`p-3 flex items-center justify-between border-b ${!isDark ? (scorecard ? 'bg-green-50' : 'bg-amber-50') : ''}`} style={isDark ? {
                          borderColor: scorecard ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)'
                        } : {}}>
                          <div className="flex items-center gap-3">
                            <UserBubble
                              userId={player.user_id}
                              picture={player.picture}
                              name={player.name}
                              bubbleColor={scorecard ? 'green' : 'orange'}
                              size="md"
                              clickable={!!player.user_id}
                            />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-[var(--ps-text-primary)]">{player.name}</p>
                                {(
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${isDark ? '' : 'bg-red-100 text-red-700'}`} style={isDark ? {
                                    background: 'rgba(239,68,68,0.2)',
                                    color: '#f87171',
                                    border: '1px solid rgba(239,68,68,0.3)'
                                  } : {}}>
                                    HCP: {handicapLabel(eventHandicap(player))}
                                  </span>
                                )}
                                {isLinkedUser && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? '' : 'bg-blue-100 text-blue-700'}`} style={isDark ? {
                                    background: 'rgba(59,130,246,0.2)',
                                    color: '#60a5fa',
                                    border: '1px solid rgba(59,130,246,0.3)'
                                  } : {}}>Linked</span>
                                )}
                              </div>
                              {scorecard && (
                                <p className={`text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>Total: {scorecard.total_score}</p>
                              )}
                            </div>
                          </div>
                          {scorecard && (
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${isDark ? '' : 'bg-green-100 text-green-700'}`} style={isDark ? {
                              background: 'rgba(34,197,94,0.2)',
                              color: '#4ade80',
                              border: '1px solid rgba(34,197,94,0.3)'
                            } : {}}>
                              Complete
                            </span>
                          )}
                        </div>
                        
                        {/* Score Entry Form - shown for creator (all players) OR for the current user's own card */}
                        {!scorecard && (event.is_creator || (player.user_id && player.user_id === user?.user_id)) && (
                          <div className={`p-4 ${!isDark ? 'bg-white border-t border-amber-100' : ''}`} style={isDark ? {
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 100%)'
                          } : {}}>
                            <ScoreEntryForm
                              courseId={event.course_id}
                              eventHoles={event.holes || 18}
                              initialScores={currentScores}
                              onScoresChange={(newScores) => updatePlayerScore(player.player_id, newScores)}
                              playerName={player.name}
                              showHolesSelector={false}
                              showVoiceOption={true}
                              showScanOption={true}
                              autoSave={true}
                              autoSaveEndpoint={`${API}/skins/events/${eventId}/scorecards/draft`}
                              autoSavePayload={(scores) => ({
                                player_id: player.player_id,
                                hole_scores: scores.map(s => s === '' ? null : parseInt(s) || null)
                              })}
                              submitButtonText="Finalize Score"
                              showTotalDisplay={true}
                              onSave={({ scores }) => {
                                finalizePlayerScorecard(player.player_id, scores);
                              }}
                              disabled={isSubmitting}
                              startingNine={event.starting_nine || 'front'}
                              getActualHoleNumber={(idx) => event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1}
                              holePars={event.hole_pars || []}
                              holeYardages={event.hole_yardages || []}
                              showYardages={event.hole_yardages && event.hole_yardages.length > 0}
                              darkMode={isDark}
                              isMultiPlayer={(event.players || []).length > 1}
                              onAllPlayersCompletedHole={handlePlayerScoreSaved}
                              showHoleInfoCards={(event.players || []).length <= 1}
                            />
                          </div>
                        )}
                        
                        {/* View-only scorecard for other players when not creator */}
                        {!scorecard && !event.is_creator && player.user_id !== user?.user_id && (
                          <div className={`p-4 text-center ${!isDark ? 'bg-stone-50 border-t' : ''}`} style={isDark ? {
                            background: 'rgba(0,0,0,0.2)',
                            borderTop: '1px solid rgba(255,255,255,0.1)'
                          } : {}}>
                            <p className="text-sm text-[var(--ps-text-muted)]">
                              Waiting for {player.name} to submit their scorecard
                            </p>
                          </div>
                        )}
                        
                        {/* Show completed scores with stroke indicators */}
                        {scorecard && (() => {
                          // Calculate min handicap from all players - treat null as 0
                          const minHandicap = handicapBaseline(event.players || []);
                          const playerInitial = player.name?.charAt(0).toUpperCase() || '?';
                          const holeHandicaps = event.hole_handicaps || [];
                          const holePars = event.hole_pars || [];
                          
                          // Helper to render score cell with proper shape
                          const renderScoreCell = (score, holeIdx, displayHole) => {
                            const strokesOnHole = showHandicapStrokes ? getStrokesOnHole(eventHandicap(player), minHandicap, holeIdx, holeHandicaps) : 0;
                            const holePar = holePars[holeIdx] || 4;
                            const style = getScoreStyle(score, holePar);
                            
                            // Get colors based on score type
                            const getColors = () => {
                              switch (style.scoreType) {
                                case 'eagle':
                                  return isDark 
                                    ? { bg: 'rgba(234,179,8,0.3)', color: '#fde047', border: 'rgba(234,179,8,0.5)' }
                                    : { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
                                case 'birdie':
                                  return isDark
                                    ? { bg: 'rgba(34,197,94,0.25)', color: '#4ade80', border: 'rgba(34,197,94,0.5)' }
                                    : { bg: '#dcfce7', color: '#166534', border: '#86efac' };
                                case 'par':
                                  return isDark
                                    ? { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', border: 'rgba(59,130,246,0.4)' }
                                    : { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
                                case 'bogey':
                                  return isDark
                                    ? { bg: 'rgba(251,191,36,0.2)', color: '#fbbf24', border: 'rgba(251,191,36,0.3)' }
                                    : { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
                                default:
                                  return isDark
                                    ? { bg: 'rgba(239,68,68,0.2)', color: '#f87171', border: 'rgba(239,68,68,0.3)' }
                                    : { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                              }
                            };
                            
                            const colors = getColors();
                            
                            // Shape classes
                            const shapeClass = style.shape === 'circle' || style.shape === 'circle-double'
                              ? 'rounded-full'
                              : style.shape === 'square'
                                ? 'rounded-none'
                                : 'rounded';
                            
                            return (
                              <div key={`hole-${holeIdx}`}>
                                <div className="mb-1 flex flex-col items-center text-[var(--ps-text-muted)]">
                                  <span className="text-[10px]">{displayHole}</span>
                                  {strokesOnHole > 0 && (
                                    <span className={`text-[9px] font-bold leading-none ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                      -{strokesOnHole}{playerInitial}
                                    </span>
                                  )}
                                </div>
                                <div 
                                  className={`w-7 h-7 mx-auto ${shapeClass} flex items-center justify-center font-bold text-xs ${
                                    style.shape === 'circle-double' ? 'ring-2 ring-yellow-400' : ''
                                  }`}
                                  style={{
                                    background: colors.bg,
                                    color: colors.color,
                                    border: `2px solid ${colors.border}`
                                  }}
                                >
                                  {score}
                                </div>
                              </div>
                            );
                          };
                          
                          return (
                            <div className={`p-3 ${!isDark ? 'bg-white border-t' : ''}`} style={isDark ? {
                              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 100%)'
                            } : {}}>
                              {/* Edit button for creator */}
                              {event.is_creator && (
                                <div className="flex justify-end mb-2">
                                  <button
                                    onClick={() => {
                                      setEditingScorePlayer(player.player_id);
                                      setEditScores([...(scorecard.hole_scores || [])]);
                                    }}
                                    className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1"
                                  >
                                    <Edit className="w-3 h-3" />
                                    Edit Scores
                                  </button>
                                </div>
                              )}
                              
                              {/* Editing mode */}
                              {editingScorePlayer === player.player_id ? (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-9 gap-1">
                                    {editScores.slice(0, 9).map((score, idx) => {
                                      const displayHole = event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1;
                                      return (
                                        <div key={idx} className="text-center">
                                          <div className="text-[10px] text-[var(--ps-text-muted)] mb-1">{displayHole}</div>
                                          <input
                                            type="number"
                                            min="1"
                                            max="15"
                                            value={score || ''}
                                            onChange={(e) => {
                                              const newScores = [...editScores];
                                              newScores[idx] = e.target.value ? parseInt(e.target.value) : '';
                                              setEditScores(newScores);
                                            }}
                                            className="w-7 h-7 text-center text-xs rounded border bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)]"
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {event.holes === 18 && (
                                    <div className="grid grid-cols-9 gap-1">
                                      {editScores.slice(9, 18).map((score, idx) => (
                                        <div key={idx + 9} className="text-center">
                                          <div className="text-[10px] text-[var(--ps-text-muted)] mb-1">{idx + 10}</div>
                                          <input
                                            type="number"
                                            min="1"
                                            max="15"
                                            value={score || ''}
                                            onChange={(e) => {
                                              const newScores = [...editScores];
                                              newScores[idx + 9] = e.target.value ? parseInt(e.target.value) : '';
                                              setEditScores(newScores);
                                            }}
                                            className="w-7 h-7 text-center text-xs rounded border bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)]"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex gap-2 justify-end">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingScorePlayer(null);
                                        setEditScores([]);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => updatePlayerScorecard(player.player_id, editScores)}
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="grid grid-cols-9 gap-1 text-center text-xs">
                                    {(scorecard.hole_scores || []).slice(0, 9).map((score, idx) => {
                                      const displayHole = event.starting_nine === 'back' && event.holes === 9 ? idx + 10 : idx + 1;
                                      return renderScoreCell(score, idx, displayHole);
                                    })}
                                  </div>
                                  {event.holes === 18 && scorecard.hole_scores?.length > 9 && (
                                    <div className="grid grid-cols-9 gap-1 text-center text-xs mt-2">
                                      {(scorecard.hole_scores || []).slice(9, 18).map((score, idx) => 
                                        renderScoreCell(score, idx + 9, idx + 10)
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>

                {/* Shared Scan Card - Only for creator - Scan once for all players */}
                {event.is_creator && !sharedScanResult && (
                  <div className={`rounded-xl p-4 mt-4 ${!isDark ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-300' : ''}`} style={isDark ? {
                    background: 'linear-gradient(145deg, rgba(59,130,246,0.15) 0%, rgba(99,102,241,0.1) 100%)',
                    border: '2px dashed rgba(59,130,246,0.4)',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
                  } : {}}>
                    <div className="text-center">
                      <Camera className={`w-10 h-10 mx-auto mb-2 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                      <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-blue-900'}`}>Scan Scorecard for All Players</h4>
                      <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
                        Upload one photo of your scorecard - we'll detect all {(event.players || []).length} players
                      </p>
                      <div className="flex justify-center gap-2">
                        <label className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-9 px-4 py-2 border border-input shadow-sm hover:bg-accent hover:text-accent-foreground bg-white">
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => handleSharedScan(e.target.files?.[0])}
                            disabled={scanningShared}
                          />
                          <Camera className="w-4 h-4" />
                          Take Photo
                        </label>
                        <label className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-9 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleSharedScan(e.target.files?.[0])}
                            disabled={scanningShared}
                          />
                          {scanningShared ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Camera className="w-4 h-4" />
                              Upload Photo
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Shared Scan Results - Only for creator - Match scanned players to event players */}
                {event.is_creator && sharedScanResult && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-300 rounded-xl p-4 mt-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <div>
                          <h4 className="font-semibold text-green-900">
                            Scorecard Scanned - {sharedScanResult.players?.length || 0} Players Detected
                          </h4>
                          <p className="text-sm text-green-700">Click a scanned player to apply their scores</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={clearSharedScan} className="text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Scanned preview */}
                    {sharedScanPreview && (
                      <div className="mb-4">
                        <img 
                          src={sharedScanPreview} 
                          alt="Scanned scorecard" 
                          className="w-full h-32 object-cover rounded-lg border border-green-200"
                        />
                      </div>
                    )}
                    
                    {/* Scanned players grid */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {(sharedScanResult.players || []).map((scannedPlayer, idx) => (
                        <div 
                          key={`scanned-player-${scannedPlayer.player_name || idx}`}
                          className="bg-[var(--ps-bg-surface)] border border-green-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-[var(--ps-text-primary)] text-sm">
                              {scannedPlayer.player_name || `Player ${idx + 1}`}
                            </span>
                            <span className="text-xs text-green-600 font-semibold">
                              {scannedPlayer.total_score || '--'}
                            </span>
                          </div>
                          <p className="text-xs text-[var(--ps-text-muted)] font-mono mb-2">
                            {(scannedPlayer.hole_scores || []).slice(0, 5).map(s => s ?? '-').join(', ')}...
                          </p>
                          {/* Dropdown to assign to event player */}
                          <Select onValueChange={(playerId) => applyScannedScoresToPlayer(idx, playerId)}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Apply to player..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(event.players || []).filter(p => !getPlayerScorecard(p.player_id)).map(p => (
                                <SelectItem key={p.player_id} value={p.player_id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-xs text-center text-green-600">
                      After assigning, review scores below and click "Finalize Score" for each player
                    </p>
                  </div>
                )}

                {/* Calculate Button */}
                {event.is_creator && (event.scorecards || []).length >= 2 && event.status === 'active' && !event.results && (
                  <div className={`pt-4 mt-4 ${isDark ? '' : 'border-t'}`} style={isDark ? { borderTop: '1px solid rgba(255,255,255,0.1)' } : {}}>
                    {/* All scorecards submitted banner */}
                    {(event.scorecards || []).length === (event.players || []).length && (
                      <div className={`mb-4 p-4 rounded-xl text-center ${isDark ? 'bg-green-900/30 border border-green-700/50' : 'bg-green-50 border border-green-200'}`}>
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Trophy className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                          <span className={`font-bold ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                            All Scorecards Submitted!
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-green-400/80' : 'text-green-600'}`}>
                          Ready to calculate results and determine the winner
                        </p>
                      </div>
                    )}
                    <Button 
                      onClick={calculateResults} 
                      disabled={calculating}
                      className={`w-full font-semibold ${isDark ? 'text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                      style={isDark ? {
                        background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
                        boxShadow: '0 4px 12px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                      } : {}}
                      data-testid="calculate-skins-btn"
                    >
                      {calculating ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Calculate Skins Results'}
                    </Button>
                    <p className="text-xs text-center mt-2 text-[var(--ps-text-muted)]">
                      {(event.scorecards || []).length} of {(event.players || []).length} scorecards submitted
                    </p>
                  </div>
                )}
                
                {/* Results calculated banner */}
                {event.results && (
                  <div className={`pt-4 mt-4 ${isDark ? '' : 'border-t'}`} style={isDark ? { borderTop: '1px solid rgba(255,255,255,0.1)' } : {}}>
                    <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-amber-900/30 border border-amber-700/50' : 'bg-amber-50 border border-amber-200'}`}>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Trophy className={`w-6 h-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                        <span className={`font-bold text-lg ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                          Results Calculated!
                        </span>
                      </div>
                      <p className={`text-sm mb-3 ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
                        View the Results tab to see winners and payouts
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('results')}
                        className={`${isDark ? 'border-amber-600 text-amber-400 hover:bg-amber-900/30' : 'border-amber-600 text-amber-700 hover:bg-amber-50'}`}
                      >
                        View Results
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <Card>
          <CardContent className="p-4">
            {!event.results ? (
              <div className="text-center py-8 text-[var(--ps-text-secondary)]">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p>No results yet</p>
                <p className="text-sm">Results will appear after scorecards are submitted and calculated</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Winner Banner - Handles ties */}
                {(() => {
                  const payouts = calculatePayouts();
                  if (!payouts) return null;
                  
                  const sortedPlayers = Object.entries(event.results.player_skins || {}).filter(([id]) => !event.use_handicaps || hasHandicap(eventHandicap(event.players.find(p => p.player_id === id)))).sort(([, a], [, b]) => b - a);
                  if (sortedPlayers.length === 0 || sortedPlayers[0][1] === 0) return null;
                  
                  // Find all players tied for first place
                  const topSkins = sortedPlayers[0][1];
                  const winners = sortedPlayers.filter(([, skins]) => skins === topSkins);
                  const isTie = winners.length > 1;
                  
                  const winnerNames = winners.map(([id]) => playerMap[id]?.name || 'Unknown');
                  const totalWinningsEach = topSkins * payouts.skinWorth;
                  const netWinningsEach = totalWinningsEach - payouts.perPlayerContribution;
                  
                  return (
                    <button
                      onClick={() => {
                        setWinnerData({
                          name: winnerNames.join(' & '),
                          skins: topSkins,
                          amount: netWinningsEach,
                          grossAmount: totalWinningsEach,
                          buyIn: payouts.perPlayerContribution,
                          playerId: winners[0][0],
                          isTie: isTie,
                          winnerCount: winners.length,
                          isCoins: payouts.isCoins,
                          entryFeeType: payouts.entryFeeType
                        });
                        setShowWinnerModal(true);
                      }}
                      className={`w-full rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] cursor-pointer relative overflow-hidden ${
                        payouts.isCoins 
                          ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500'
                          : 'bg-gradient-to-r from-green-400 via-emerald-400 to-green-500'
                      }`}
                      data-testid="winner-banner"
                    >
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjIpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMyIvPjwvZz48L3N2Zz4=')] opacity-30" />
                      <div className="relative">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          {payouts.isCoins ? <Coins className="w-6 h-6 text-amber-800" /> : <Trophy className="w-6 h-6 text-green-800" />}
                          <span className={`font-bold text-lg ${payouts.isCoins ? 'text-amber-900' : 'text-green-900'}`}>
                            {isTie ? 'TIE - CO-WINNERS' : 'WINNER'}
                          </span>
                          {payouts.isCoins ? <Coins className="w-6 h-6 text-amber-800" /> : <Trophy className="w-6 h-6 text-green-800" />}
                        </div>
                        <p className={`text-2xl font-black ${payouts.isCoins ? 'text-amber-900' : 'text-green-900'}`}>
                          {winnerNames.join(' & ')}
                        </p>
                        <p className={`font-semibold ${payouts.isCoins ? 'text-amber-800' : 'text-green-800'}`}>
                          {topSkins} Skin{topSkins !== 1 ? 's' : ''} • Net +{formatAmount(netWinningsEach, payouts.isCoins)} {isTie ? 'each' : ''}
                        </p>
                        <p className={`text-xs mt-1 animate-pulse ${payouts.isCoins ? 'text-amber-700' : 'text-green-700'}`}>Tap to celebrate!</p>
                      </div>
                    </button>
                  );
                })()}

                {/* Payout Breakdown - Simplified: Who owes the winner */}
                {(() => {
                  const payouts = calculatePayouts();
                  if (!payouts) return null;
                  
                  // Handle multiple winners (ties)
                  const winnersData = payouts.playerData.filter(p => p.amountOwed < 0);
                  const winnerNames = winnersData.map(w => w.name).join(' & ');
                  
                  const toggleExpand = (playerId) => {
                    setExpandedPayments(prev => ({
                      ...prev,
                      [playerId]: !prev[playerId]
                    }));
                  };
                  
                  return (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                        {payouts.isCoins ? <Coins className="w-5 h-5 text-amber-600" /> : <DollarSign className="w-5 h-5 text-green-600" />}
                        Settlement {payouts.isCoins && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Golf Coins</span>}
                      </h3>
                      <div className="space-y-2">
                        {/* Players who owe money */}
                        {payouts.playersWhoOwe.map((player, idx) => {
                          const details = payouts.paymentDetails[player.playerId];
                          const isExpanded = expandedPayments[player.playerId];
                          
                          return (
                            <div key={`payout-${player.playerId}`} className="bg-white/70 rounded-lg overflow-hidden">
                              <button 
                                onClick={() => toggleExpand(player.playerId)}
                                className="w-full flex items-center justify-between p-3 hover:bg-white/90 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <UserBubble
                                    userId={player.userId}
                                    picture={player.picture}
                                    name={player.name}
                                    bubbleColor="blue"
                                    size="sm"
                                    clickable={!!player.userId}
                                  />
                                  <div className="text-left">
                                    <span className="font-medium text-[var(--ps-text-primary)]">{player.name}</span>
                                    <p className="text-xs text-[var(--ps-text-muted)]">
                                      Buy-in: {formatAmount(player.potContribution, payouts.isCoins)}
                                      {player.skinsWon > 0 && (
                                        <span className="text-green-600"> • Won {player.skinsWon} skin{player.skinsWon !== 1 ? 's' : ''} ({formatAmount(player.winnings, payouts.isCoins)})</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="text-right">
                                    <span className="text-lg font-bold text-red-600">
                                      {formatAmount(player.amountOwed, payouts.isCoins)}
                                    </span>
                                    <p className="text-xs text-[var(--ps-text-muted)]">owes</p>
                                  </div>
                                  <ChevronDown className={`w-5 h-5 text-[var(--ps-text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </button>
                              
                              {/* Expanded payment details */}
                              {isExpanded && details?.pays?.length > 0 && (
                                <div className="px-3 pb-3 border-t border-[var(--ps-border)]">
                                  <p className="text-xs text-[var(--ps-text-muted)] mt-2 mb-1 font-medium">Pay to:</p>
                                  {details.pays.map((payment, pIdx) => (
                                    <div key={pIdx} className="flex items-center justify-between py-1.5 px-2 bg-red-50 rounded mb-1">
                                      <span className="text-sm text-[var(--ps-text-primary)]">→ {payment.to}</span>
                                      <span className="text-sm font-semibold text-red-600">{formatAmount(payment.amount, payouts.isCoins)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Show winners too with their winnings */}
                        {winnersData.map((winner, idx) => {
                          const details = payouts.paymentDetails[winner.playerId];
                          const isExpanded = expandedPayments[winner.playerId];
                          
                          return (
                            <div key={`winner-${idx}`} className="bg-amber-50 rounded-lg border border-amber-200 overflow-hidden">
                              <button 
                                onClick={() => toggleExpand(winner.playerId)}
                                className="w-full flex items-center justify-between p-3 hover:bg-amber-100/50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <UserBubble
                                    userId={winner.userId}
                                    picture={winner.picture}
                                    name={winner.name}
                                    bubbleColor="yellow"
                                    size="sm"
                                    clickable={!!winner.userId}
                                  />
                                  <div className="text-left">
                                    <span className="font-medium text-amber-900">{winner.name}</span>
                                    <span className="ml-2 text-xs bg-amber-300 text-amber-800 px-1.5 py-0.5 rounded font-semibold">WINNER</span>
                                    <p className="text-xs text-amber-700">
                                      Buy-in: {formatAmount(winner.potContribution, payouts.isCoins)} • Won {winner.skinsWon} skins ({formatAmount(winner.winnings, payouts.isCoins)})
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="text-right">
                                    <span className="text-lg font-bold text-green-600">
                                      +{formatAmount(Math.abs(winner.amountOwed), payouts.isCoins)}
                                    </span>
                                    <p className="text-xs text-green-600">collects</p>
                                  </div>
                                  <ChevronDown className={`w-5 h-5 text-amber-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </button>
                              
                              {/* Expanded collection details */}
                              {isExpanded && details?.receives?.length > 0 && (
                                <div className="px-3 pb-3 border-t border-amber-200">
                                  <p className="text-xs text-amber-700 mt-2 mb-1 font-medium">Collect from:</p>
                                  {details.receives.map((payment, pIdx) => (
                                    <div key={pIdx} className="flex items-center justify-between py-1.5 px-2 bg-green-50 rounded mb-1">
                                      <span className="text-sm text-[var(--ps-text-primary)]">← {payment.from}</span>
                                      <span className="text-sm font-semibold text-green-600">{formatAmount(payment.amount, payouts.isCoins)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700">Skin Value:</span>
                          <span className="font-bold text-green-800">{formatAmount(payouts.skinWorth, payouts.isCoins)} ({event.players?.length || 0} players)</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700">Total Pot:</span>
                          <span className="font-bold text-green-800">{formatAmount(payouts.totalPot, payouts.isCoins)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700">Per Player Buy-in:</span>
                          <span className="font-bold text-green-800">{formatAmount(payouts.perPlayerContribution, payouts.isCoins)}</span>
                        </div>
                        {/* Info about guest players */}
                        {event.players?.some(p => !p.user_id || p.is_guest) && (
                          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                            <p className="text-blue-800 font-medium">ℹ️ Playing with guests</p>
                            <p className="text-blue-600 mt-1">Your win/loss is tracked. Guest settlements show as "Guest" in your records.</p>
                          </div>
                        )}
                        <Link 
                          to="/settlements" 
                          className="mt-4 w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Go to My Settlements
                        </Link>
                      </div>
                    </div>
                  );
                })()}

                {/* Leaderboard - All Players */}
                <div>
                  <h3 className="font-semibold text-[var(--ps-text-primary)] mb-3 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Leaderboard
                  </h3>
                  <div className="space-y-2">
                    {(() => {
                      const payouts = calculatePayouts();
                      if (!payouts) return null;
                      
                      return payouts.playerData.map((player, idx) => {
                        const isWinner = player.amountOwed < 0;
                        const skinsWon = player.skinsWon;
                        return (
                          <div key={player.playerId} className={`flex items-center justify-between p-3 rounded-lg ${
                            isWinner ? 'bg-amber-100 ring-2 ring-amber-300' : 'bg-[var(--ps-bg-elevated)]'
                          }`}>
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                isWinner ? 'bg-amber-500 text-white' : 'bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]'
                              }`}>
                                {idx + 1}
                              </span>
                              <UserBubble
                                userId={player.userId}
                                picture={player.picture}
                                name={player.name}
                                bubbleColor={isWinner ? 'yellow' : 'green'}
                                size="sm"
                                clickable={!!player.userId}
                              />
                              <div>
                                <span className="font-medium">{player.name}</span>
                                {isWinner && <span className="ml-2 text-xs text-amber-700 font-semibold">WINNER</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xl font-bold text-amber-600">{skinsWon}</span>
                              <span className="text-sm text-[var(--ps-text-muted)] ml-1">skin{skinsWon !== 1 ? 's' : ''}</span>
                              <p className="text-xs text-[var(--ps-text-muted)]">{formatAmount(player.winnings, payouts.isCoins)} won</p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  {event.results.unclaimed_skins > 0 && (
                    <p className="text-sm text-[var(--ps-text-muted)] mt-2 text-center">
                      {event.results.unclaimed_skins} unclaimed skin(s) from final carryover
                    </p>
                  )}
                </div>

                {/* Sync Scores to Recent Rounds */}
                {event.status === 'completed' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-1">Add to Recent Rounds</h4>
                        <p className="text-sm text-blue-700 mb-3">
                          Sync these scores to your Recent Rounds so they count toward your handicap calculation.
                        </p>
                        <Button
                          onClick={syncScoresToHistory}
                          disabled={syncingScores || scoresSynced}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                          data-testid="sync-scores-btn"
                        >
                          {syncingScores ? (
                            <div className="spinner w-4 h-4 border-white border-t-transparent mr-2" />
                          ) : scoresSynced ? (
                            <Check className="w-4 h-4 mr-2" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          {scoresSynced ? 'Scores Synced!' : 'Sync to My Rounds'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hole-by-Hole Results */}
                <div>
                  <h3 className="font-semibold text-[var(--ps-text-primary)] mb-3">Hole-by-Hole</h3>
                  <div className="overflow-x-auto">
                    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(event.holes, 9)}, minmax(40px, 1fr))` }}>
                      {(event.results.hole_results || []).slice(0, 9).map((hr) => (
                        <div key={`hr-${hr.hole}`} className={`p-2 rounded text-center text-xs ${
                          hr.carryover ? 'bg-[var(--ps-bg-elevated)]' : 'bg-green-100'
                        }`}>
                          <div className="font-bold text-[var(--ps-text-secondary)]">H{hr.hole}</div>
                          {hr.carryover ? (
                            <div className="text-[var(--ps-text-muted)]">C/O</div>
                          ) : (
                            <>
                              <div className="font-medium text-green-700 truncate">
                                {playerMap[hr.winner_id]?.name?.split(' ')[0] || '?'}
                              </div>
                              {hr.skins_won > 1 && (
                                <div className="text-amber-600 font-bold">+{hr.skins_won}</div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {event.holes === 18 && (
                      <div className="grid gap-1 mt-2" style={{ gridTemplateColumns: 'repeat(9, minmax(40px, 1fr))' }}>
                        {(event.results.hole_results || []).slice(9, 18).map((hr) => (
                          <div key={`hr-${hr.hole}`} className={`p-2 rounded text-center text-xs ${
                            hr.carryover ? 'bg-[var(--ps-bg-elevated)]' : 'bg-green-100'
                          }`}>
                            <div className="font-bold text-[var(--ps-text-secondary)]">H{hr.hole}</div>
                            {hr.carryover ? (
                              <div className="text-[var(--ps-text-muted)]">C/O</div>
                            ) : (
                              <>
                                <div className="font-medium text-green-700 truncate">
                                  {playerMap[hr.winner_id]?.name?.split(' ')[0] || '?'}
                                </div>
                                {hr.skins_won > 1 && (
                                  <div className="text-amber-600 font-bold">+{hr.skins_won}</div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Winner Celebration Modal */}
      <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-2 border-amber-300 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            {/* Confetti/sparkle animation background */}
            <div className="absolute top-4 left-4 animate-bounce delay-100">
              <Sparkles className="w-6 h-6 text-amber-400" />
            </div>
            <div className="absolute top-8 right-8 animate-bounce delay-300">
              <Sparkles className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="absolute bottom-12 left-8 animate-bounce delay-500">
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
            <div className="absolute bottom-8 right-12 animate-bounce delay-200">
              <Sparkles className="w-7 h-7 text-yellow-400" />
            </div>
            <div className="absolute top-1/2 left-4 animate-pulse">
              <PartyPopper className="w-6 h-6 text-amber-400" />
            </div>
            <div className="absolute top-1/3 right-4 animate-pulse delay-300">
              <PartyPopper className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
          
          <div className="text-center relative z-10 py-4">
            <div className="mb-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Trophy className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h2 className="text-3xl font-black text-amber-900 mb-2 animate-fade-in">
              CONGRATULATIONS!
            </h2>
            
            {winnerData && (
              <>
                <p className="text-4xl font-black text-amber-700 mb-4 animate-fade-in">
                  {winnerData.name}
                </p>
                
                <div className="bg-[var(--ps-bg-surface)]/80 rounded-xl p-4 mb-4 shadow-inner">
                  <p className="text-lg text-[var(--ps-text-secondary)] mb-1">Net Profit</p>
                  <p className="text-5xl font-black text-green-600 mb-2 flex items-center justify-center gap-2">
                    {winnerData.isCoins ? (
                      <><Coins className="w-8 h-8 text-amber-500" /> +{Math.round(winnerData.amount).toLocaleString()}</>
                    ) : winnerData.entryFeeType === 'wallet' ? (
                      <><Wallet className="w-8 h-8 text-green-500" /> +${winnerData.amount.toFixed(2)}</>
                    ) : (
                      <>+${winnerData.amount.toFixed(2)}</>
                    )}
                  </p>
                  {winnerData.grossAmount && (
                    <div className="text-sm text-[var(--ps-text-muted)] space-y-1">
                      <p>Won: {winnerData.isCoins 
                        ? `${Math.round(winnerData.grossAmount).toLocaleString()} coins`
                        : `$${winnerData.grossAmount.toFixed(2)}`
                      } ({winnerData.skins} skin{winnerData.skins !== 1 ? 's' : ''})</p>
                      <p>Buy-in: -{winnerData.isCoins
                        ? `${Math.round(winnerData.buyIn).toLocaleString()} coins`
                        : `$${winnerData.buyIn?.toFixed(2) || '0.00'}`
                      }</p>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-center gap-2">
                  <span className="text-2xl animate-bounce">🏆</span>
                  <span className="text-2xl animate-bounce delay-100">⛳</span>
                  <span className="text-2xl animate-bounce delay-200">{winnerData.isCoins ? '🪙' : '💰'}</span>
                  <span className="text-2xl animate-bounce delay-300">🎉</span>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowWinnerModal(false)} 
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold"
            >
              View Full Results
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Tab */}
      {activeTab === 'settings' && event.is_creator && (
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* Game Settings - Editable */}
            <div className="p-4 bg-[var(--ps-bg-elevated)] rounded-lg">
              <h3 className="font-semibold text-[var(--ps-text-primary)] mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Game Settings
              </h3>
              
              {/* Holes Selection */}
              <div className="mb-4">
                <Label className="text-sm text-[var(--ps-text-secondary)] mb-2 block">Number of Holes</Label>
                <div className="flex gap-2">
                  <Button
                    variant={event.holes === 9 ? "default" : "outline"}
                    onClick={async () => {
                      try {
                        await axios.patch(`${API}/skins/events/${event.event_id}`, { holes: 9 });
                        toast.success("Changed to 9 holes");
                        fetchEvent();
                      } catch (err) {
                        toast.error("Failed to update holes");
                      }
                    }}
                    className={`flex-1 ${event.holes === 9 ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                    data-testid="holes-9-btn"
                  >
                    9 Holes
                  </Button>
                  <Button
                    variant={event.holes === 18 ? "default" : "outline"}
                    onClick={async () => {
                      try {
                        await axios.patch(`${API}/skins/events/${event.event_id}`, { holes: 18 });
                        toast.success("Changed to 18 holes");
                        fetchEvent();
                      } catch (err) {
                        toast.error("Failed to update holes");
                      }
                    }}
                    className={`flex-1 ${event.holes === 18 ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                    data-testid="holes-18-btn"
                  >
                    18 Holes
                  </Button>
                </div>
              </div>

              {/* Front/Back Selection - Only show for 9-hole games */}
              {event.holes === 9 && (
                <div className="mb-4">
                  <Label className="text-sm text-[var(--ps-text-secondary)] mb-2 block">Which Nine?</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={event.starting_nine === 'front' ? "default" : "outline"}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/skins/events/${event.event_id}`, { starting_nine: 'front' });
                          toast.success("Changed to Front 9 (Holes 1-9)");
                          fetchEvent();
                        } catch (err) {
                          toast.error("Failed to update");
                        }
                      }}
                      className={`flex-1 ${event.starting_nine === 'front' ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                      data-testid="front-9-btn"
                    >
                      Front 9
                      <span className="text-xs opacity-70 ml-1">(1-9)</span>
                    </Button>
                    <Button
                      variant={event.starting_nine === 'back' ? "default" : "outline"}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/skins/events/${event.event_id}`, { starting_nine: 'back' });
                          toast.success("Changed to Back 9 (Holes 10-18)");
                          fetchEvent();
                        } catch (err) {
                          toast.error("Failed to update");
                        }
                      }}
                      className={`flex-1 ${event.starting_nine === 'back' ? 'bg-[var(--ps-primary)] text-white' : ''}`}
                      data-testid="back-9-btn"
                    >
                      Back 9
                      <span className="text-xs opacity-70 ml-1">(10-18)</span>
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Skin Value - Only show if NOT using new prize pool system */}
              {!(event.entry_fee && event.entry_fee > 0 && event.entry_fee_type) && (
              <div className="mb-4">
                <Label className="text-sm text-[var(--ps-text-secondary)] mb-2 block">Skin Value (per hole)</Label>
                {editingSkinValue ? (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ps-text-muted)]">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tempSkinValue}
                        onChange={(e) => setTempSkinValue(e.target.value)}
                        className="pl-7"
                        placeholder="5.00"
                      />
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => updateSkinValue(tempSkinValue)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Save
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setEditingSkinValue(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-[var(--ps-text-primary)]">${event.skin_value?.toFixed(2) || '5.00'}</span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setTempSkinValue(event.skin_value?.toString() || '5');
                        setEditingSkinValue(true);
                      }}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                )}
              </div>
              )}
              
              {/* Show Prize Pool info if using new system */}
              {event.entry_fee && event.entry_fee > 0 && event.entry_fee_type && (
                <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                  <Label className="text-sm text-amber-800 font-semibold mb-2 block flex items-center gap-2">
                    {event.entry_fee_type === 'coins' ? (
                      <><Coins className="w-4 h-4" /> Golf Coins Entry</>
                    ) : (
                      <><Wallet className="w-4 h-4" /> Golf Wallet Entry</>
                    )}
                  </Label>
                  <p className="text-lg font-bold text-amber-700">
                    {event.entry_fee_type === 'coins' 
                      ? `${event.entry_fee.toLocaleString()} coins per player`
                      : `$${event.entry_fee} per player`
                    }
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    Total pot: {event.entry_fee_type === 'coins'
                      ? `${(event.entry_fee * (event.players?.length || 0)).toLocaleString()} coins`
                      : `$${event.entry_fee * (event.players?.length || 0)}`
                    }
                  </p>
                </div>
              )}
              
              {/* USE HANDICAPS TOGGLE - Main setting for skins calculation */}
              <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-green-800 font-semibold">Apply Handicaps to Skins</Label>
                    <p className="text-xs text-green-600 mt-1">
                      {event.use_handicaps 
                        ? "Handicap strokes are applied when determining skin winners" 
                        : "Raw scores only - no handicap adjustments"}
                    </p>
                  </div>
                  <button
                    onClick={toggleUseHandicaps}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      event.use_handicaps ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    data-testid="use-handicaps-toggle"
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      event.use_handicaps ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {event.use_handicaps && (
                  <p className="text-xs text-green-700 mt-2 border-t border-green-200 pt-2">
                    💡 Players receive strokes on the most difficult holes based on the difference between their handicap and the lowest handicap in the group.
                  </p>
                )}
              </div>
              
              {/* Show Handicap Strokes Display Toggle - Visual only */}
              {event.use_handicaps && (
                <div className="mb-4 p-3 bg-[var(--ps-bg-surface)] rounded-lg border border-[var(--ps-border)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-[var(--ps-text-primary)] font-medium">Show Stroke Indicators</Label>
                      <p className="text-xs text-[var(--ps-text-muted)]">Display "-1C" markers on scorecards</p>
                    </div>
                    <button
                      onClick={() => setShowHandicapStrokes(!showHandicapStrokes)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        showHandicapStrokes ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        showHandicapStrokes ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Recalculate Results Button - Show if game is complete AND changes were made */}
              {event.status === 'complete' && (
                <div className={`mb-4 p-3 rounded-lg border ${needsRecalculate ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-200'}`}>
                  {needsRecalculate && (
                    <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Settings Changed - Recalculation Required!
                    </div>
                  )}
                  <h4 className={`text-sm font-medium mb-2 ${needsRecalculate ? 'text-red-800' : 'text-amber-900'}`}>
                    {needsRecalculate ? 'Recalculate Now' : 'Recalculate Results'}
                  </h4>
                  <p className={`text-xs mb-3 ${needsRecalculate ? 'text-red-600' : 'text-amber-700'}`}>
                    {needsRecalculate 
                      ? "You've made changes since results were calculated. Click below to update results with new settings."
                      : "Use this if you've edited scores or handicaps after the game was calculated."}
                  </p>
                  <Button
                    onClick={() => {
                      recalculateResults();
                      setNeedsRecalculate(false);
                    }}
                    disabled={calculating}
                    className={`w-full ${needsRecalculate ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
                    data-testid="recalculate-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${calculating ? 'animate-spin' : ''}`} />
                    {calculating ? 'Recalculating...' : needsRecalculate ? '⚠️ Recalculate Results Now' : 'Recalculate Results'}
                  </Button>
                </div>
              )}

              {/* Event Info */}
              <div className="text-sm text-[var(--ps-text-secondary)] space-y-1 pt-2 border-t border-[var(--ps-border)]">
                <p><strong>Name:</strong> {event.name}</p>
                <p><strong>Date:</strong> {event.date}</p>
                <p><strong>Course:</strong> {event.course_name || 'Not specified'}</p>
                <p><strong>Status:</strong> <span className={`font-medium ${event.status === 'complete' ? 'text-green-600' : event.status === 'active' ? 'text-blue-600' : 'text-gray-500'}`}>{event.status}</span></p>
              </div>
            </div>

            {/* Course Data Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Course Data
              </h3>
              <div className="text-sm text-blue-800 space-y-1 mb-3">
                <p><strong>Course:</strong> {event.course_name || 'Not set'}</p>
                {event.hole_pars && event.hole_pars.length > 0 && (
                  <div>
                    <strong>Hole Pars:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {event.hole_pars.slice(0, event.holes || 18).map((par, idx) => (
                        <span key={idx} className="w-6 h-6 flex items-center justify-center bg-[var(--ps-bg-surface)] rounded text-xs font-medium border border-blue-200">
                          {par}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={refreshCourseData} 
                  variant="outline" 
                  size="sm"
                  disabled={refreshingCourse}
                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshingCourse ? 'animate-spin' : ''}`} />
                  {refreshingCourse ? 'Refreshing...' : 'Refresh from GolfCourseAPI'}
                </Button>
                <Button 
                  onClick={() => setShowCourseLookup(true)} 
                  variant="outline" 
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Change Course
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button onClick={deleteEvent} variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Skins Game
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Add Players Modal

export default SkinsEventDetail;
